/* Code generated by balancegen from client/src/ui/hub.css.tmpl.
   DO NOT EDIT: edit the .tmpl sibling and run scripts/gen-data.sh.

   Only the wide-pane breakpoint is substituted, because a media query
   cannot read a custom property (R362). Every colour is a token of
   client/src/gen/tokens.css. */

@import '../gen/tokens.css';

/* The hub (ui/hub/, R229; mockup round fifteen B, card 02).

   The hub is the menu between the landing screen and a world. It is up
   before the canvas exists, so it lives outside `#ui` and carries its own
   sheet, exactly as `#landing` does. Everything else is the house chrome of
   `ui-map-interface.md` §2: the monospace stack, uppercase labels with
   letter-spacing, panels on the panel ink with the accent edge, and a
   44 px minimum target because a telephone is a target platform.

   This sheet takes its colours from the tokens `ui/styles.css` declares
   and turns no literal of its own. A theme rewrites those tokens on the
   document root (R243), so a hard-coded `rgba()` of the accent or of an
   ink would stay cyan under every other banner.

   The shape is card 02 of round fifteen: the five sections a player presses
   every visit are a dock at the bottom, inside the thumb's reach, and the
   five occasional ones are a small strip under the header. The pane between
   them scrolls, and a fade sits at its foot while content is still below.

   The variables are `styles.css`'s own — that file is always loaded — with
   three the hub adds for the tier badges. */

#hub {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: var(--mono);
  font-size: 12px;
  line-height: 1.5;
  color: var(--text);
  background:
    radial-gradient(120% 90% at 50% 0%, rgb(var(--accent-rgb) / 0.09), transparent 55%),
    rgb(var(--ink-rgb));
  -webkit-text-size-adjust: 100%;

  /* One vertical rhythm for the whole menu (R231). Two full-width blocks,
     two stacked half-width cards and the two columns of a split are all one
     gap apart. The value is the split's 12 px and not the column's old
     10 px, because the split's gap is also the horizontal gutter between
     its two columns: one number keeps the two directions equal. A panel
     spaces a block with this and never with a `margin-top` of its own. */
  --hub-gap: 12px;

  /* The type floor (R1739, DESIGN.md "Typography"). A size under 11 px
     reads `max(var(--type-floor), …)`: at rest no text of the hub is under
     the 9 px Micro step, and the `(pointer: coarse)` block at the foot of
     this sheet raises the floor to 11 px. The ☸ OPTIONS kit of the
     SETTINGS panel reads the same floor. */
  --type-floor: 9px;
}

#hub * { box-sizing: border-box; }

#hub .violet { color: var(--violet); }
#hub .rust { color: var(--amber); }
#hub .alloy { color: var(--alloy); }

/* --- the header ------------------------------------------------------- */

#hub .top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  height: 54px;
  padding: 0 14px;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.14);
  /* The solid page ink (R1772): iOS paints the status band over the
     header in the page ink, and the header at 86 % cover read in another
     tint under it. */
  background: rgb(var(--ink-rgb));
}
/* The sign and the wordmark are the header lockup (brand.md §2, R1645;
   mockup round 079, card 16). Every length is a fraction of the sign box
   `--lockup-h`: the name has a cap height of `cap` of the box and stands
   `gap` of the box after it. With a line height of 1, the face's own
   metrics put the middle of the cap height on the middle of the box. The
   face is the one `styles.css` declares. */
#hub .top .lockup {
  --lockup-h: 22px;
  display: flex;
  align-items: center;
  gap: calc(var(--lockup-h) * 0.25);
  flex: none;
}
#hub .top .glyph { width: var(--lockup-h); height: var(--lockup-h); flex: none; }
/* The dev corner of a page of the dev identity (brand.md §15.2, R2880):
   the sign wears it where no DEV cell of the environment tag stands
   beside it. A header with a tag (`tagged`) hides it on a wide screen,
   where the `.env` cell shows; the narrow block below hides that cell and
   so leaves the corner. */
@media (min-width: 901px) {
  #hub .top .lockup.tagged .devc { display: none; }
}
/* The room after the wordmark is the wordmark's own, so it goes with it on
   a narrow screen where the wordmark is hidden. Without it the name reads
   as the second half of AQUAPHOBIA (R231). The margin takes back the
   tracking after the last letter, so the room is the room alone. */
#hub .top .wm {
  font: 500 calc(var(--lockup-h) * 0.6027) / 1 "Aqua Lockup", var(--mono);
  letter-spacing: 0.3em;
  color: var(--game-bright);
  white-space: nowrap;
  margin-right: calc(18px - 0.3em);
}
/* The environment tag stands between the lockup and the player name
   (§16.3, R2872): a chip of two cells in amber, the environment on an
   amber ground and the stage in the outline, at the height of the tier
   badge. It never wraps, and a narrow screen keeps the stage cell alone,
   so the name keeps the room (the narrow block below). */
#hub .top .envtag {
  display: inline-flex;
  flex: none;
  height: 22px;
  border: 1px solid rgb(var(--amber-rgb) / 0.6);
  border-radius: 3px;
  overflow: hidden;
  font: 600 max(var(--type-floor), 10px) / 1 var(--mono);
  letter-spacing: 1px;
  color: var(--amber);
  white-space: nowrap;
}
#hub .top .envtag > span { display: inline-flex; align-items: center; padding: 0 7px; }
#hub .top .envtag .env { background: rgb(var(--amber-rgb) / 0.22); }
#hub .top .envtag .stage { border-left: 1px solid rgb(var(--amber-rgb) / 0.6); }
#hub .top .sp { flex: 1; }
#hub .pname {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  font-size: 12px;
  letter-spacing: 1px;
  color: var(--text);
}
#hub .pname b { font-weight: normal; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* The faction badge stands before the name (§16.3, R1781). The image
   carries its own size, and the line height of 0 keeps the row from
   growing round it. It stays on every width. */
#hub .pname .fbadge { display: flex; flex: none; line-height: 0; }

/* The tier badge is wide enough to read at a glance on a wide screen;
   that was the point of the change in round fifteen. It is a tag and no
   control, so it has the corner of a tag and no ground: a filled box of
   26 px read as a button (R1739). A telephone gives it its content width
   alone, so the name keeps the room (the narrow block below). */
#hub .tierbadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  height: 22px;
  min-width: 104px;
  padding: 0 12px;
  border: 1px solid;
  border-radius: 3px;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 3px;
}
#hub .tierbadge.free { color: var(--dim); border-color: rgb(var(--faint-rgb) / 0.5); }
#hub .tierbadge.supporter { color: var(--gold); border-color: rgb(var(--gold-rgb) / 0.5); }
#hub .tierbadge.premium { color: var(--violet); border-color: rgb(var(--violet-rgb) / 0.5); }

#hub .top button {
  font: inherit;
  min-height: 44px;
  padding: 0 10px;
  color: var(--dim);
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  letter-spacing: 2px;
  font-size: max(var(--type-floor), 10px);
  cursor: pointer;
}
/* The rename pencil is a small mark beside the name and not a second
   control the size of the badge. The painted box is 26 px; the target is
   still 44 px, and a pseudo-element carries it, because a 44 px painted box
   is what was too big (R231). */
#hub .top button.icon {
  position: relative;
  width: 26px;
  min-width: 26px;
  height: 26px;
  min-height: 26px;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  color: var(--accent);
  border-color: var(--edge);
  background: rgb(var(--accent-rgb) / 0.1);
}
#hub .top button.icon::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
}
/* Every hover look of the hub stands in a `(hover: hover)` block, in its
   own place in the sheet (R1693, ui-map-interface.md §8): a telephone keeps
   the hover look on a control after a tap. */
@media (hover: hover) {
  #hub .top button:hover { color: var(--accent); }
  #hub .top button.quiet:hover { color: var(--bad); }
}
/* The armed SIGN OUT control (R696). It wears the same danger ground the
   armed buttons wear, so one arming reads the same everywhere. */
#hub .top button.armed { color: var(--bad); background: rgb(var(--bad-rgb) / 0.18); border-color: rgb(var(--bad-rgb) / 0.7); }

/* The rename tray drops out of the header. It exists exactly once: after
   the change lands the pencil is gone for good (R226).

   The tray holds no look of its own (R249). Its content is a `card` with a
   `form`, a `lede` and a row of `pill`s, which is the vocabulary every
   panel in the pane uses, so the two steps of the rename read as part of
   this page. The tray itself only spaces the card and separates it from the
   strip below. The card sits on the panel ink and the tray behind it stays
   the sheet, which is the same pair the pane shows. */
#hub .rename {
  display: flex;
  flex-direction: column;
  gap: var(--hub-gap);
  flex: none;
  padding: 10px 14px;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.14);
  background: rgb(var(--sheet-rgb));
  /* The tray drops over the strip and the top of the pane, under the
     54 px header, and takes no room of the column. The pane keeps its
     height, so the PLAY tiles neither move nor shrink while it stands
     (R2151). It scrolls when it is taller than the screen. */
  position: absolute;
  top: 54px;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: calc(100% - 54px);
  overflow-y: auto;
}
/* The tray's notice and its `card` have no margins of their own: the gap
   above spaces them, exactly as `--hub-gap` spaces two blocks in the pane. */
#hub .rename .hint { font-size: max(var(--type-floor), 10px); color: var(--dim); line-height: 1.45; }
#hub .rename > .err { margin-top: 0; font-size: 12px; color: var(--bad); line-height: 1.45; }
/* The box takes the whole width of the card. A rename form holds one field
   and no second control beside it, so the `1 1 160px` of a two-field form
   would leave the box short of the card's right edge. */
#hub .rename .form input { flex: 1 1 100%; }

/* --- the utility strip ------------------------------------------------ */

/* The strip is the quiet row of the two (R1741): a shorter row, Dim
   names, and the open tab marked by its ink and an underline, never by a
   filled block. The dock is the one row of primary sections. A tab draws
   its focus ring inside its own box (the tab block below, R1739). */
#hub .strip {
  display: flex;
  flex: none;
  overflow: hidden;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.14);
  background: rgb(var(--ink-rgb) / 0.6);
}

/* --- the pane --------------------------------------------------------- */

#hub .pane { position: relative; flex: 1; min-height: 0; }
/* The dock stands before the pane in the markup, so the Tab key reaches the
   five primary sections first (R1738); `order` keeps it at the foot. The
   head, the rename tray and the strip keep order 0. */
#hub .pane { order: 1; }
#hub .dock { order: 2; }
/* The live region of the hub (R1738): a screen reader reads it, and the
   page never paints it. */
#hub .live {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
/* The pane is a column, so a block that asks to grow can take the room the
   pane has left. That is what the mode tiles do (R231). Nothing shrinks:
   a section taller than the pane scrolls, and a card is never squeezed. */
#hub .paneinner {
  position: absolute;
  inset: 0;
  padding: 14px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
#hub .paneinner > * { flex: 0 0 auto; }
/* The block that carries the mode tiles is the one exception: it grows,
   and the tile grid inside it grows with it. Both wrappers are named,
   because a panel's root is a `page` (R231) and a `col` is what a panel
   nests inside one. A selector that knows only one of the two leaves the
   tile rows on their 150 px floor with a tall pane empty below them
   (R232 §8.1). */
#hub .paneinner > .page:has(> .tiles),
#hub .paneinner > .col:has(> .tiles) { flex: 1 1 auto; }
#hub .page > .tiles,
#hub .col > .tiles { flex: 1 1 auto; }
/* The maintenance window ahead, over the mode tiles (deployment.md §6.1).
   It is gold, because it is a SCHEDULE and not a fault: the worlds are
   still running, and a player who presses a tile now plays until the
   window opens. It takes no press. */
#hub .page > .maint {
  margin-bottom: 10px;
  padding: 8px 12px;
  color: var(--gold);
  letter-spacing: 1px;
  background: rgb(var(--gold-rgb) / 0.08);
  border-left: 2px solid rgb(var(--gold-rgb) / 0.55);
}
#hub .paneinner.wide { padding: 16px 20px; }
/* The fade is shown only while content is still below. A fade over the end
   of a list says "there is more" when there is not (R229). */
#hub .pane .fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 44px;
  z-index: 9;
  pointer-events: none;
  opacity: 0;
  transition: opacity 140ms linear;
  background: linear-gradient(180deg, rgb(var(--ink-rgb) / 0), rgb(var(--ink-rgb) / 0.92));
}
#hub .pane.more .fade { opacity: 1; }

/* --- the dock --------------------------------------------------------- */

#hub .dock {
  display: flex;
  flex: none;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.16);
  background: rgb(var(--ink-rgb) / 0.92);
  padding-bottom: env(safe-area-inset-bottom);
}

/* --- one tab, in either chrome ---------------------------------------- */

/* The name is the whole button (R231). It carries no glyph, so it is one
   row, and it is set at the size a control's label takes and not at the
   size of a caption: 12 px in the dock, which is the rank a player presses
   every visit, and 11 px in the utility strip. That is the step above the
   card heading's 10 px and beside the section heading's 11 px, and the name
   is upper case with letter-spacing, so it grows less than a figure would.
   The row heights hold the 44 px target.

   The selector names the strip and the dock. A bare `#hub .t` also took
   the line text of the chronicle, the achievement rows and the chat, which
   use the class `t` too, and set it as a centred tab in Faint (R1739).
   The resting name is Dim, because it is the text of a control. */
#hub :is(.strip, .dock) > .t {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 4px;
  font: inherit;
  letter-spacing: 2px;
  color: var(--dim);
  background: none;
  border: 0;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
}
/* A strip name wraps to a second line where it does not fit, as
   EINSTELLUNGEN does on a telephone, and is never cut (R1741). The row is
   44 px, the target of a finger. */
#hub .strip .t {
  height: 44px;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 1.5px;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  border-bottom: 1px solid transparent;
}
#hub .strip .t .n { overflow-wrap: anywhere; }
#hub .dock .t { height: 56px; font-size: 12px; border-top: 2px solid transparent; }
#hub .dock > .t .n { overflow: hidden; text-overflow: ellipsis; }
/* The strip clips its overflow, so the ring of a tab stands inside the
   tab. The dock takes the same ring, so the two rows read alike. */
#hub :is(.strip, .dock) > .t:focus-visible { outline-offset: -2px; }
#hub .strip .t.on { color: var(--text); border-bottom-color: rgb(var(--accent-rgb) / 0.6); }
#hub .dock .t.on { color: var(--accent); border-top-color: var(--accent); background: rgb(var(--accent-rgb) / 0.08); }
/* The dot rides beside the name. A name that grows moves an absolutely
   placed dot with it, so the dot is a child of the row. */
#hub :is(.strip, .dock) > .t .dot {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: var(--gold);
}

/* --- content primitives (ui/hub/panel.ts) ----------------------------- */

/* The heading levels (R2048): the section heading is an `h1`; a page
   title, a card heading, a sub-page name and a PLAY tile name are an `h2`;
   a sub-header of a page is an `h3`. Each heading must draw as the `div` or `span` it replaced, so this rule
   takes the browser's own heading margin, size and weight away. It has no
   specificity, so every class rule of a heading still wins over it. The
   arcade lobby draws the section shell too. */
:where(#hub, #arc-lobby) :where(h1, h2, h3) { margin: 0; font-size: inherit; font-weight: inherit; line-height: inherit; }

#hub .h1 { font-size: 11px; letter-spacing: 4px; color: var(--accent); margin-bottom: 10px; }
#hub .h1 .sub { margin-left: 8px; font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--dim); }

/* `page` is the whole content of a section, and it is the one place the
   spacing between two top-level blocks comes from (R231). A block inside
   it carries no margin of its own: an `h1`'s margin-bottom or a pill row's
   margin-top would add to the gap and make the first one larger than the
   rest, so the sheet clears them here. */
#hub .page { display: flex; flex-direction: column; gap: var(--hub-gap); min-width: 0; }
#hub .page > * { margin-top: 0; margin-bottom: 0; }

#hub .col { display: flex; flex-direction: column; gap: var(--hub-gap); min-width: 0; }
#hub .split { display: grid; grid-template-columns: 1fr 1fr; gap: var(--hub-gap); align-items: start; }
/* NEWS with one card in its list column: the two cards of the row are of
   one height (R2151). A longer list keeps the start, so the community
   card does not stand as tall as the whole list. */
#hub .split:has(> .col > .card.newsc:only-child) { align-items: stretch; }
#hub .split:has(> .col > .card.newsc:only-child) > .col > .card { flex: 1 1 auto; }
/* The three plan columns of SHOP on a wide pane; the panel stacks them as a
   .col on a narrow one, so no breakpoint is needed here. */
#hub .split3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--hub-gap); align-items: start; }

#hub .card {
  position: relative;
  padding: 11px 12px;
  border: 1px solid var(--edge);
  border-radius: 8px;
  background: var(--panel);
}
#hub .card > .h {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 3px;
  color: var(--accent);
}
#hub .card.seat-event > .h { color: var(--violet); }
#hub .card.seat-clanwar > .h { color: var(--bad); }
#hub .card > .h .g { color: var(--dim); }
#hub .card > .h .sp { flex: 1; }
/* The corner text stays on one row, so a long title wraps and the age of
   a NEWS article does not (R1560). */
#hub .card > .h .more { font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--dim); white-space: nowrap; }
/* A section of a page (§16.16 "The page body"): no box. The sub-header
   is the `.o-sec` of the kit (`options.css`), and a page with one section
   draws its content alone under the page title. */
#hub .card.sect { padding: 0; border: 0; border-radius: 0; background: none; }
#hub .card.sect > .o-sec:first-child { margin-top: 4px; }
#hub .card.sect > .o-sec .tier { margin-bottom: 0; }
/* The figure that stood in the corner of the old card heading, as the
   first line of a page with one section (`figLine`): the quiet face of
   `.card > .h .more`. */
#hub .card.sect > .pfig {
  margin: 0 0 8px; font-size: max(var(--type-floor), 9px); letter-spacing: var(--t-cap-track); color: var(--dim);
  overflow-wrap: anywhere;
}
/* A tier tag of a page stands at the right of the page title. */
#hub .hs-ptitle .o-pt > .tier { margin: 0 0 0 auto; }
/* A tier tag stands on the right of a card heading (R1547). */
#hub .card > .h .tier { margin-bottom: 0; }
/* The YOUR PLAN line over the rows of the plan an account holds (SHOP). */
#hub .planhead { margin-bottom: 8px; font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--good); }

#hub .row {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 4px 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08);
  font-size: 11px;
}
#hub .row:last-child { border-bottom: 0; }
#hub .row .k {
  flex: 1;
  min-width: 0;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 1px;
  color: var(--dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
#hub .row .v { color: var(--text); white-space: nowrap; font-variant-numeric: tabular-nums; }
/* The key sheet of SUPPORT: a long key or a long action wraps in its own
   half of the row, so a telephone shows no sideways scroll. */
#hub .keyrows .row { align-items: flex-start; }
#hub .keyrows .row .k { flex: 0 1 42%; overflow: visible; text-overflow: clip; white-space: normal; overflow-wrap: anywhere; }
#hub .keyrows .row .v { flex: 1 1 0; min-width: 0; white-space: normal; overflow-wrap: anywhere; text-align: right; }
#hub .row .v.acc { color: var(--accent); }
#hub .row .v.gold { color: var(--gold); }
/* The Honour of a mode reads in the ink of its PLAY tile (R2151). */
#hub .row .v.event { color: var(--violet); }
#hub .row .v.clanwar { color: var(--bad); }
#hub .row .v.bad { color: var(--bad); }
#hub .row .v.good { color: var(--good); }
/* NEXT WINDOW names the object first, on one line (R1752). The key keeps
   its width, the site takes the ellipsis, and the time with its zone mark
   and the span never shrink. `pre` keeps the space of the joint at the
   head of the time part. */
#hub .row.wrow .k { flex: none; }
#hub .row.wrow .v { flex: 1 1 auto; min-width: 0; display: flex; justify-content: flex-end; }
#hub .row.wrow .ws { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
#hub .row.wrow .wt { flex: none; white-space: pre; }
#hub .row.self { border-top: 1px solid var(--edge); margin-top: 6px; padding-top: 8px; }
#hub .row.self .k { color: var(--accent); }
/* A row that carries a control of its own is 44 px tall and centres its
   parts (R257). The painted pill inside it is smaller than that, and the
   pill's own 44 px target then fills the row exactly, so the target of one
   row never reaches into the row above or below it. */
#hub .row.act { min-height: 44px; align-items: center; }
/* The return briefing on the seat card (ui-map-interface.md §12.6, R1605,
   card 05 of round 076): the rows of the absence under WHILE YOU WERE OUT,
   behind a gold rule. A row is no press: the seat is not resumed. */
#hub .aw { margin: 2px 0 4px; padding: 4px 0 2px 10px; border-left: 2px solid rgb(var(--gold-rgb) / 0.5); }
/* The siege against the faction at the head of the seat card (R1743): the
   briefing's urgent row, alone, before the rows of the card. */
#hub .threat { margin: 0 0 6px; }
#hub :is(.aw, .threat) .brow { display: flex; align-items: center; gap: 9px; padding: 4px 2px; }
#hub :is(.aw, .threat) .brow .g { flex: none; width: 16px; text-align: center; color: var(--dim); font-size: 13px; }
/* The drawn resource icon of a seat row (`ui/resicon.ts`, R1768), at 1em
   on the text line as on the game page. */
#hub svg.ri { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; flex: none; }
/* The detail stands under the label. */
#hub :is(.aw, .threat) .brow .t {
  flex: 1; min-width: 0; display: block; padding: 0; gap: 0;
  letter-spacing: normal; text-align: left; white-space: normal; cursor: default;
}
#hub :is(.aw, .threat) .brow .t .l { display: block; color: var(--text); font-size: max(var(--type-floor), 10.5px); }
#hub :is(.aw, .threat) .brow .t .s {
  display: block; color: var(--dim); font-size: max(var(--type-floor), 10px);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* The siege detail is read text: Dim, and it wraps on a telephone. */
#hub .threat .brow .t .s { color: var(--dim); white-space: normal; }
#hub :is(.aw, .threat) .brow .f { flex: none; color: var(--text); font-size: 12px; font-variant-numeric: tabular-nums; text-align: right; }
#hub .aw .brow .f .neg { color: var(--bad); }
#hub .aw .brow .f .pos { color: var(--good); }
#hub .aw .brow .f .muted { color: var(--dim); }
#hub :is(.aw, .threat) .brow.urgent { border-left: 2px solid var(--bad); background: rgb(var(--bad-rgb) / 0.06); padding-left: 6px; }
#hub :is(.aw, .threat) .brow.urgent :is(.g, .t .l) { color: var(--bad); }
/* The seat card takes no hue for a figure (R2044, ui-map-interface.md
   §16.6): under a banner whose accent is the Bad hue, an accent figure
   reads as an alarm. A figure that asks for action (STANDING, an idle or
   short QUEUE, a raid, an open window) and a gain or a loss of the
   briefing read in the banner's Text, the warm off-white of every other
   figure; the keys keep the banner's Dim. The siege row alone keeps the
   Bad ink. */
#hub .seats .card .row .v:is(.acc, .gold, .bad),
#hub .seats .card .aw .brow .f :is(.pos, .neg) { color: var(--text); }
/* The siege-alert line under the seat cards (R1743): quiet Dim text, and
   its fix words the text button of a tile's refusal line, with a 44 px
   hit area through `::after`. */
#hub .pushask { font-size: 12px; letter-spacing: 1px; color: var(--dim); line-height: 1.5; padding: 2px 2px 0; }
#hub .pushask .fix {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  color: var(--accent);
  text-decoration: none;
  border-bottom: 1px dotted rgb(var(--accent-rgb) / 0.35);
}
#hub .pushask .fix::after { content: ""; position: absolute; left: -8px; right: -8px; top: 50%; height: 44px; transform: translateY(-50%); }
@media (hover: hover) {
  #hub .pushask .fix:hover { border-bottom-color: var(--accent); }
}

/* A lede is a sentence, so it takes the 12 px Body type (R1739). */
#hub .lede { max-width: 72ch; font-size: 12px; color: var(--dim); line-height: 1.55; overflow-wrap: anywhere; }
/* The SUPPORT question list (R1744): four group heads, and under each one
   row a question with its answer. A question is a sentence, so it takes
   the Body type in mixed case, with no tracking and no pill. The whole row
   is the press, 44 px high at least, and it may wrap. The chevron points
   at the row's end while the answer is closed and turns down while it is
   open. The rule outweighs the look of a card's own button. */
#hub .qa-h {
  margin: 14px 0 2px;
  font-size: max(var(--type-floor), 10px);
  font-weight: 400;
  letter-spacing: 2px;
  color: var(--dim);
}
#hub .qa-h:first-child { margin-top: 2px; }
/* The HOW IT PLAYS row over the first head (R2445): a lede and a press
   for each page of the card, over a hairline. */
#hub .qa-how { padding-bottom: 12px; border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08); }
#hub .qa { border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08); }
#hub .qa:last-child { border-bottom: 0; }
#hub .qa .qa-q {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 8px 4px 8px 0;
  font: inherit;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: 0;
  text-align: left;
  color: var(--text);
  background: none;
  border: 0;
  border-radius: 0;
}
#hub .qa .qa-t { flex: 1; min-width: 0; }
#hub .qa .qa-c {
  flex: none;
  width: 7px;
  height: 7px;
  margin-right: 4px;
  border-right: 1.5px solid var(--dim);
  border-bottom: 1.5px solid var(--dim);
  transform: rotate(-45deg);
  transition: transform 0.15s ease-out;
}
#hub .qa.on .qa-q { color: var(--accent); }
#hub .qa.on .qa-c { transform: rotate(45deg); border-color: var(--accent); }
/* The list takes the whole pane (R1751), so an answer keeps a reading
   measure of its own and does not run the width of a desktop. */
#hub .qa-a { max-width: 72ch; padding: 0 0 10px; }
#hub .qa-a[hidden] { display: none; }
@media (hover: hover) {
  #hub .card .qa .qa-q:hover { color: var(--accent); background: none; }
}
@media (prefers-reduced-motion: reduce) {
  #hub .qa .qa-c { transition: none; }
}
/* The GLOSSARY card of SUPPORT (R1557): the filter field on its own line,
   then one block per term, the word over its definition. A term the filter
   drops is `hidden` in place, so the field keeps its focus. The card takes
   the whole pane under the questions, so the terms flow in columns of at
   least 260 px, three at most: three on a desktop, one on a telephone. A
   term never breaks across two columns (R1751). */
#hub .gfilter { display: block; width: 100%; max-width: 420px; box-sizing: border-box; margin: 8px 0; }
#hub .gterms { columns: 260px 3; column-gap: 28px; }
#hub .gterm { break-inside: avoid; padding: 5px 0; border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08); }
#hub .gterm .gw { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; color: var(--accent); text-transform: uppercase; }
#hub .gterm[hidden], #hub .gnone[hidden] { display: none; }

/* A row of controls of the choice kit (panel.ts ctlRow, R2011) keeps room
   under it and between its wrapped rows (R257). The row gap is larger than
   the column gap on purpose: the touch target of a pill is taller than the
   pill, so two stacked rows need the room that two pills beside each other
   do not. The bottom margin keeps the last row off whatever the card draws
   under it. `page` clears both margins on a top-level block, so this cannot
   enlarge the gap of a page. */
#hub .hctl { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 8px; margin: 8px 0 10px; }
/* The volume pick of the chronicle book: the rows of the volume list and
   the presses of the stepper bar (a pick list, which R2011 does not
   change). One press is 30 px painted, and the hand gets its 44 px from
   the pseudo-element below (R257). */
#hub .vpick {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid var(--edge);
  border-radius: 15px;
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 2px;
  color: var(--dim);
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: anywhere;
}
/* A volume pick that acts is a `<button>`, so the keyboard reaches it
   (R1698). This rule takes back each look a browser gives a button and
   `.vpick` does not state: the native face, the margin, the font, the text
   properties, the ground and the column break of Gecko. A span has these
   values, so a button paints as the span did: every computed property is
   the same in Chromium 148 and Firefox 157. `:where` keeps the weight of
   this rule at one id, so every `.vpick` rule of this sheet still wins. */
#hub :where(button.vpick[data-act]) {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  font: inherit;
  text-rendering: inherit;
  text-transform: inherit;
  text-indent: inherit;
  text-shadow: inherit;
  text-align: inherit;
  word-spacing: inherit;
  white-space: inherit;
  background: none;
  break-inside: auto;
}
/* An older Gecko draws a dotted ring inside a focused button. The ring of
   R1693 is the one mark of the keyboard focus (R1701). */
#hub :where(button.vpick[data-act])::-moz-focus-inner { border: 0; padding: 0; }
#hub button.vpick { position: relative; cursor: pointer; }
/* The 44 px target a telephone needs, carried by a pseudo-element so the
   painted press stays 30 px and the layout does not move. It grows the box
   up and down only: a neighbour beside a press is one column gap away, and
   a target that grew sideways would take that neighbour's left edge. */
#hub button.vpick::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 44px;
  transform: translateY(-50%);
}
@media (hover: hover) {
  #hub button.vpick:hover { color: var(--accent); border-color: rgb(var(--accent-rgb) / 0.45); }
}
/* The way back of a hub sub-page (panel.ts backBar, R1751): "‹ PARENT" as
   a text bar at the top left, over a hairline rule. It copies the look of
   the ‹ SETTINGS head of the OPTIONS kit (`.opt .o-back`), and the hub
   sheet holds its own copy, so a change of the kit does not move it. The
   CUP page and the chronicle book take it. */
:is(#ui, #hub) .hback { display: flex; align-items: center; border-bottom: 1px solid var(--edge); }
/* The grey name of the sub-page beside the back button (R1780). */
:is(#ui, #hub) .hback-here { font-size: 12px; letter-spacing: 0.1em; white-space: nowrap; color: var(--dim); }
:is(#ui, #hub) .hback-b {
  appearance: none;
  -webkit-appearance: none;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  margin: 0 0 0 -8px;
  padding: 0 12px 0 8px;
  font: inherit;
  font-size: 12px;
  letter-spacing: 0.1em;
  white-space: nowrap;
  color: var(--accent);
  background: none;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}
@media (hover: hover) {
  :is(#ui, #hub) .hback-b:hover { background: rgb(var(--accent-rgb) / 0.08); }
}
#hub .vpick.on { color: var(--accent); border-color: rgb(var(--accent-rgb) / 0.6); background: rgb(var(--accent-rgb) / 0.18); }

#hub .tag { padding: 2px 6px; border: 1px solid; border-radius: 3px; font-size: max(var(--type-floor), 9px); letter-spacing: 2px; }
#hub .tag.sup { color: var(--gold); border-color: rgb(var(--gold-rgb) / 0.5); }
#hub .tag.prem { color: var(--violet); border-color: rgb(var(--violet-rgb) / 0.5); }
#hub .tag.open { color: var(--good); border-color: rgb(var(--good-rgb) / 0.5); }
/* The tag of a row the account does not hold (effects.md §3, R908). It
   names no tier, because an effect is granted and never bought. */
#hub .tag.lock { color: var(--faint); border-color: rgb(var(--faint-rgb) / 0.5); }
#hub .tier { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: max(var(--type-floor), 9px); letter-spacing: 2px; }
#hub .tier .held { color: var(--good); }
#hub .tier .unheld { color: var(--faint); }

/* A gate fades the content and never the reason. A CSS filter on a parent
   cannot be undone on a child, so the class goes on the block that must
   fade and never on the card (R229). */
#hub .faded { filter: grayscale(1); opacity: 0.42; }

#hub .empty {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 2px;
  /* The reason line is read, so it takes Dim (the Dim Read Rule, R1744).
     The uncharted mark keeps Faint: it is decoration (R1742). */
  color: var(--dim);
}
#hub .empty .e1 { font-size: max(var(--type-floor), 10px); letter-spacing: var(--t-cap-track); color: var(--dim); }
#hub .empty .e2 { font-size: max(var(--type-floor), 10px); line-height: 1.5; }
/* Most reasons are capitals and stay a caption. A reason in mixed case is
   a sentence (`mc`, written by `panel.ts` `empty`), so it takes the 12 px
   Body type and no tracking (R1739). */
#hub .empty .e2.mc { font-size: 12px; letter-spacing: normal; }
/* A lede after an empty state stands one gap below its box (R2151). */
#hub .empty + .lede { margin-top: var(--hub-gap); }
/* The empty state under the placeholder slots of an empty WARDROOM roster
   stands one gap below them (R2208). */
#hub .caps + .empty { margin-top: var(--hub-gap); }
#hub .empty.bad .e1 { color: var(--bad); }
/* An empty state reads as uncharted water (ui-map-interface.md §16.15,
   R1742): the mark of `hub/uncharted.ts` in the first column, the lines and
   the one forward press in the second, on the hatch a chart prints over
   water nobody has sounded. A dashed hairline edges the patch. The hatch
   is one gradient and the mark is one small SVG, and nothing moves. The
   loading line and the failed line keep the plain `.empty`. */
:is(#ui, #hub) .empty.uw {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 0 12px;
  align-content: start;
  padding: 12px;
  border: 1px dashed rgb(var(--accent-rgb) / 0.16);
  border-radius: 4px;
  background: repeating-linear-gradient(135deg,
    rgb(var(--accent-rgb) / 0.045) 0 1px, transparent 1px 7px);
}
:is(#ui, #hub) .empty.uw .eg { grid-row: 1 / span 3; color: var(--faint); margin-top: 1px; }
:is(#ui, #hub) .empty.uw > :not(.eg) { grid-column: 2; }
:is(#ui, #hub) .empty.uw .e2 { margin-top: 4px; }
:is(#ui, #hub) .empty.uw .hctl { margin: 10px 0 2px; }

/* The apply step of one INVITE clan stands under its row (R1742). */
#hub .form.apply { margin: 0 0 10px; }

/* The arcade ladder (arcade.md §18.6, R1616): the ARCADE LADDER card of
   RECORD (round 078 card 04) and the ARCADE LADDER list of RANKS (card
   05). The ladder mark is an inline SVG of `ui/ladder.ts` in its own ink.
   The card is the one place the ladder number stands, in the large face.
   The tier name takes the text ink: the chrome holds no brighter token. */
#hub .lmark { display: block; flex: none; }
#hub .ltier { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
#hub .lad { display: flex; align-items: center; gap: 11px; margin-bottom: 8px; }
#hub .lad .lt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
#hub .ltn { font-size: 17px; letter-spacing: 4px; color: var(--text); white-space: nowrap; }
#hub .ltn small { font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--dim); margin-left: 6px; }
#hub .lnum {
  font-size: 26px; font-weight: 300; letter-spacing: 2px; line-height: 1.1;
  color: var(--text); font-variant-numeric: tabular-nums;
}
#hub .lfig { font-size: max(var(--type-floor), 9px); letter-spacing: 1.5px; line-height: 1.5; color: var(--dim); }
#hub .lbar {
  position: relative; height: 6px; border-radius: 3px; overflow: hidden;
  background: rgb(var(--accent-rgb) / 0.14);
}
#hub .lbar i { position: absolute; top: 0; bottom: 0; border-radius: 3px; background: var(--accent); }
#hub .lnx {
  display: flex; justify-content: space-between; gap: 8px; margin: 3px 0 6px;
  font-size: max(var(--type-floor), 9px); letter-spacing: 1.5px; color: var(--dim);
}
/* The line of the last rated matches: a stretched SVG of a fixed height,
   with the tier names and the end dot as HTML over it, so the text keeps
   its size on a wide card. */
#hub .lspark { position: relative; height: 64px; margin-bottom: 6px; }
#hub .lspark svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
#hub .lspark .lb {
  position: absolute; right: 2px; transform: translateY(-100%);
  font-size: max(var(--type-floor), 9px); letter-spacing: 1px; color: var(--dim);
}
#hub .lspark .ldot {
  position: absolute; width: 5px; height: 5px; border-radius: 50%;
  background: var(--text); transform: translate(-50%, -50%);
}
#hub .row.lgroup { border-bottom: 0; padding-top: 8px; }
#hub .row.lgroup .v { font-size: max(var(--type-floor), 9px); letter-spacing: 1px; color: var(--dim); }
#hub .lrk {
  display: grid; grid-template-columns: 5.5ch auto 1fr auto; gap: 0 9px; align-items: center;
  padding: 4px 0; border-bottom: 1px solid rgb(var(--accent-rgb) / 0.07); font-size: 11px;
}
#hub .lrk .n { color: var(--dim); text-align: right; font-variant-numeric: tabular-nums; }
#hub .lrk .nm { min-width: 0; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#hub .lrk .b { font-size: max(var(--type-floor), 9.5px); letter-spacing: 1px; color: var(--dim); white-space: nowrap; }
#hub .lrk.me { background: rgb(var(--accent-rgb) / 0.08); }
#hub .lrk.me .nm, #hub .lrk.me .b { color: var(--accent); }
#hub .lrk.gap { border-top: 1px dashed var(--edge); margin-top: 4px; }
#hub .lede.lnote { margin-top: 6px; font-size: max(var(--type-floor), 10px); color: var(--dim); }
#hub .lede.lpin { margin-top: 6px; }

/* The captain tiles of one roster (R1207, R1547, ui-map-interface.md
   §16.9). The grid has three columns, which the ROSTERS page widens to
   columns of at least 104 px (the WARDROOM block below), and the panel draws
   `CAPTAIN_SLOTS_MAX` slots at least, so a roster stands as 3 + 3 and
   every roster card holds the same block. A free slot is a dashed outline.
   The tile height follows its content; a tile is not square. */
#hub .caps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
#hub .cap {
  min-width: 0;
  padding: 7px 6px;
  border: 1px solid var(--edge);
  border-radius: 5px;
  background: rgb(var(--accent-rgb) / 0.05);
  text-align: center;
}
#hub .cap.slot { border-style: dashed; background: none; }
#hub .cap.slot .p { border-style: dashed; background: none; }
/* The captain portrait (R1180). The gradient is the placeholder circle that
   stands until the render is ready; the image then covers it inside the
   ring. */
#hub .cap .p {
  width: 26px;
  height: 26px;
  margin: 0 auto 5px;
  border: 1px solid var(--edge);
  border-radius: 50%;
  overflow: hidden;
  background: radial-gradient(circle at 40% 35%, rgb(var(--accent-rgb) / 0.35), rgb(var(--panel-rgb) / 0.9));
}
#hub .cap .p img { display: block; width: 100%; height: 100%; }
#hub .cap .n { font-size: max(var(--type-floor), 9px); letter-spacing: 1px; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#hub .cap .a { font-size: max(var(--type-floor), 9px); color: var(--dim); }
/* With the designer key, a tile is a press that opens the captain in the
   Captain Designer (R1240). The open captain wears the accent edge. */
#hub .cap.press { cursor: pointer; }
#hub .cap.on { border-color: rgb(var(--accent-rgb) / 0.7); background: rgb(var(--accent-rgb) / 0.16); }

/* --- the Captain Designer (monetization.md §3.1, R1240) ---------------- */

/* The form stands in two columns on a wide pane: the name, the portrait
   and the backstory on the left, the voice lines and the presses on the
   right (R1547). The narrow block stacks them. */
#hub .cdsplit { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--hub-gap); align-items: start; }
#hub .cd { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
#hub .cd input[type="text"], #hub .cd textarea { width: 100%; box-sizing: border-box; }
#hub .cd textarea {
  font: inherit;
  font-size: 12px;
  line-height: 1.5;
  min-height: 96px;
  padding: 8px 10px;
  resize: vertical;
  color: var(--text);
  background: rgb(var(--ink-rgb) / 0.7);
  border: 1px solid var(--edge);
  border-radius: 4px;
}
#hub .cd .cdh {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 6px;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 2px;
  color: var(--dim);
}
#hub .cd .cdc { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; color: var(--dim); }
#hub .cd .cdc.bad, #hub .cd .cde { color: var(--bad); }
#hub .cd .cde { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; line-height: 1.5; }
#hub .cd .cdn { font-size: max(var(--type-floor), 10px); letter-spacing: 2px; color: var(--accent); }
#hub .cd .cdv { display: flex; align-items: center; gap: 6px; }
#hub .cd .cdv input { flex: 1 1 auto; min-width: 0; }
#hub .cd .cdv .cdc { flex: none; min-width: 52px; text-align: right; }
/* A face of the portrait grid is a preview tile of the choice kit
   (R2011): the round portrait over its word. */
#hub button.ch-tile > .cdpic { display: flex; justify-content: center; padding: 5px 0; }
/* A livery of the Fleet Designer (monetization.md §3.2, R1252) is a preview
   tile too. The preview stands on the deep ground, so the dark step of an
   option shows the ground through the hull, as the map shows the water. */
#hub button.ch-tile > .lvimg {
  display: block;
  width: 100%;
  height: 52px;
  padding: 4px 0;
  box-sizing: border-box;
  object-fit: contain;
  background: rgb(var(--deep-rgb) / 0.9);
}
/* --- the founding form of CLAN › START ONE (R2155, ui-map-interface.md
   §16.11) -------------------------------------------------------------------
   The faction cards stand beside the founding chart and the form, and a
   narrow pane stacks the three. A card is a radio of one group: the badge,
   the whole faction name (it wraps and is never cut), the ink and the
   edge dash, and the radio mark at its end. `--fk` is the faction ink as
   three channels, which `clanfound.ts` writes on the card, so no colour
   stands here. The card is a `button` in a `.card`, so its rules name
   `.cf` and win over the look of a card's own button. */
#hub .cf { display: flex; flex-direction: column; gap: 12px; }
#hub .cf .o-sec { margin-top: 0; }
#hub .cf-row { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 16px; }
#hub .cf-cards { display: flex; flex: 1 1 320px; flex-direction: column; gap: 8px; min-width: 0; }
#hub .cf-side { display: flex; flex: 1 1 300px; flex-direction: column; gap: 16px; min-width: 0; max-width: 480px; }
#hub .cf button.cf-card {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  /* The 56 px badge, 6 px above and under it and the edge: a card of
     70 px, so the five cards and the chart fit a 1440 x 900 screen
     (R2208). */
  min-height: 70px;
  padding: 6px 14px;
  font: inherit;
  letter-spacing: normal;
  text-align: start;
  color: var(--text);
  background: rgb(var(--accent-rgb) / 0.03);
  border: 1px solid var(--edge);
  border-radius: 8px;
  cursor: pointer;
}
@media (hover: hover) {
  #hub .cf button.cf-card:hover { background: rgb(var(--fk) / 0.07); border-color: rgb(var(--fk) / 0.45); }
}
#hub .cf button.cf-card.on {
  background: rgb(var(--fk) / 0.1);
  border-color: rgb(var(--fk));
  box-shadow: 0 0 0 1px rgb(var(--fk) / 0.35);
}
#hub .cf-pic { display: flex; flex: none; }
#hub .cf-badge { display: block; }
#hub .cf-txt { display: flex; flex: 1 1 auto; flex-direction: column; gap: 6px; min-width: 0; }
#hub .cf-name {
  font-size: var(--t-title-size);
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: 0.04em;
  color: rgb(var(--fk));
  overflow-wrap: break-word;
}
#hub .cf-edge { display: flex; align-items: center; gap: 8px; min-width: 0; }
#hub .cf-ink { flex: none; width: 14px; height: 14px; border-radius: 3px; background: rgb(var(--fk)); }
#hub .cf-dash { display: block; flex: 0 1 auto; min-width: 0; height: auto; }
#hub .cf-dot {
  flex: none;
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid var(--faint);
}
#hub .cf button.cf-card.on .cf-dot {
  border-color: rgb(var(--fk));
  background: radial-gradient(circle, rgb(var(--fk)) 0 3.5px, transparent 4px);
}
/* The founding chart: the uncharted water on the deep ground, its cells
   in dashed Faint hairlines, as the empty state draws uncharted water
   (R1742). The picture keeps its zoom and shrinks to a narrow pane. */
#hub .cf-chart { display: flex; flex-direction: column; gap: 8px; }
#hub svg.cf-map {
  display: block;
  max-width: 100%;
  height: auto;
  background: rgb(var(--deep-rgb));
  border: 1px solid var(--edge);
  border-radius: 8px;
}
#hub .cf-water { fill: none; stroke: var(--faint); stroke-width: 0.8; stroke-dasharray: 2 3; opacity: 0.5; }
#hub .cf-form .form { margin-top: 0; }
/* The name box takes the wide share of the row. The tag box is as wide as
   the longest tag the rules allow: `--tag-ch` (from CLAN_TAG_MAX_LEN, in
   `clan.ts`) characters of the mono face with their 1px tracking, one
   character for the caret, and the padding and the edge of the box. A
   pane too narrow for both puts the tag box under the name box, at its
   narrow width. */
#hub .cf-names .field.cf-fname { flex: 1 1 200px; }
#hub .cf-names .field.cf-ftag {
  flex: 0 0 auto;
  width: calc((var(--tag-ch) + 1) * (1ch + 1px) + 22px);
}
/* --- end of the founding form ---------------------------------------------- */
/* FLEET LIVERY (R2186): each group block `.fd-lvg` states its wide and its
   narrow column count (`liveryCols` of fleetcard.ts), and the block of the
   page `.fd-lv` is a container, so a page of 520 px or more takes
   the wide count: 5 x 2 tiles on a desktop page, 2 x 5 on a telephone.
   NONE stands in a block of the first group's counts, one cell wide. The
   picture keeps the 64 x 44 shape of its cell at the tile width, and the
   name wraps to a second line and never ends in an ellipsis. The rules
   name `.fd-lv`, so no other preview tile moves. */
#hub .fd-lv { container: fd-lv / inline-size; }
#hub .fd-lvg > .ch-tiles {
  grid-template-columns: repeat(var(--lv-n, 2), minmax(0, 1fr));
  max-width: none;
}
@container fd-lv (min-width: 520px) {
  #hub .fd-lvg > .ch-tiles { grid-template-columns: repeat(var(--lv-w, 5), minmax(0, 1fr)); }
}
#hub .fd-lv button.ch-tile { min-height: var(--target-finger); }
#hub .fd-lv button.ch-tile > img.lvimg { height: auto; max-height: 104px; }
#hub .fd-lv button.ch-tile > b {
  flex: 1 0 auto;
  min-height: 36px;
  padding: 5px 8px;
  white-space: normal;
  line-height: 1.3;
  letter-spacing: 0.06em;
}
#hub .fd-lv button.ch-tile > b > .ch-tn { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: break-word; }
#hub .cdpic .p {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: radial-gradient(circle at 40% 35%, rgb(var(--accent-rgb) / 0.35), rgb(var(--panel-rgb) / 0.9));
}
#hub .cdpic .p img { display: block; width: 100%; height: 100%; }


/* --- the mode tiles --------------------------------------------------- */

/* The four mode tiles (R231).

   The arrangement is fixed and never derived from a track size:

     under 600 px   one column. Two tiles on a 360 px screen leave each of
                    them about 170 px, which is narrower than the mode name
                    and the call to action were drawn for.
     600 px and up  two columns, so four tiles are 2x2 at every width the
                    menu has above a telephone.

   It is never three across. `auto-fit` gave 3+1 at about 900 px, which is
   the arrangement the owner refused by name.

   4x1 is reserved. It is right only when something sits under the tiles and
   takes the room a single row leaves. Nothing does today, so no width
   ships it: add the content first, then the rule.

   The tiles keep a picture in them, so a row has a floor and a ceiling.
   Under 150 px a tile shows a letterbox slice of its preview and nothing a
   reader can name; over 300 px it is a poster. Between the two the rows
   take the room the pane has, which is what fills the space a single row
   used to leave under it. */
#hub .tiles {
  display: grid;
  gap: var(--hub-gap);
  grid-template-columns: 1fr;
  grid-auto-rows: minmax(150px, 1fr);
}
@media (min-width: 600px) {
  #hub .tiles {
    grid-template-columns: 1fr 1fr;
    /* Two rows at the 300 px ceiling, and the gap between them. */
    max-height: calc(300px * 2 + var(--hub-gap));
  }
}
/* The seat cards under the tiles (R1561). One column on a telephone; two
   from the width where the tiles stand two across, so a seat card is as
   wide as the tile above it. A single card keeps half the width. */
#hub .seats {
  display: grid;
  gap: var(--hub-gap);
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
}
@media (min-width: 600px) {
  #hub .seats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
/* The floor and the ceiling of a tile are the grid's rows above, so the
   tile itself asks for no height. */
#hub .tile {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 9px;
  background: var(--console-ground);
}
/* The mode reel fills the tile and crops to it (R1535); its poster shows
   until the video plays, and stays under reduced motion. */
#hub .tile video.reel {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  pointer-events: none;
}
#hub .tile .veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgb(var(--deep-rgb) / 0.15), rgb(var(--deep-rgb) / 0.9) 64%);
}
#hub .tile .body {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 11px 12px;
}
/* The kicker stands on a reel in motion, so it takes Dim (R1739). */
#hub .tile .kick { position: relative; isolation: isolate; font-size: max(var(--type-floor), 9px); letter-spacing: var(--t-cap-track); color: var(--dim); }
/* A kicker and a line wrap at any symbol (R3178): the name of a custom
   world is the host's own text, and one long word must not run out of
   the tile. The body stands at the bottom of the tile, so the text grows
   up from the bottom left. */
#hub .tile :is(.kick, .st, .why) { overflow-wrap: anywhere; }
/* A scrim under the kicker and the corner tag (R1751): a band of the ink
   at a low alpha that fades out at its edges, so a bright sprite of the
   reel never runs through the words. `isolation` keeps the band behind
   the words and over the reel. */
#hub .tile .kick::before {
  content: '';
  position: absolute;
  z-index: -1;
  left: -12px;
  right: -12px;
  top: -12px;
  bottom: -8px;
  background: linear-gradient(180deg, rgb(var(--ink-rgb) / 0), rgb(var(--ink-rgb) / 0.55) 45%, rgb(var(--ink-rgb) / 0.55) 75%, rgb(var(--ink-rgb) / 0));
  pointer-events: none;
}
#hub .tile .nm { margin: 2px 0 3px; font-size: 19px; letter-spacing: 4px; }
/* The tile foot is a grid of two columns: the state or reason line, and
   the CTA (R1741). The text column is `minmax(0, 1fr)`, so it yields to the
   button whatever word the button carries, in every language, and a long
   line wraps under the name and never under the button. The button sits on
   the last line of the text. Its negative top margin takes the button's
   height above one text line out of the foot, so the button rises beside
   the name and a tile with a PLAY puts its name at the height of a locked
   tile (R2560). */
#hub .tile .tf {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  column-gap: 10px;
}
#hub .tile .tf > :only-child { grid-column: 1 / -1; }
#hub .tile .st { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; color: var(--accent); line-height: 1.4; }
/* A siege against the seat's faction takes the Bad ink on the tile of the
   held seat (R1752), as it does at the head of the seat card. */
#hub .tile .st.siege { color: var(--bad); }
/* A refusal is quiet text, so the live PLAY of a neighbour tile stays the
   loudest mark of the panel (R1734). Its fix words are a text button: the
   accent with a dotted underline of the accent at 35 %. The press takes a
   44 px hit area through `::after`. The area ends at the tile edge (the
   body's 11 px bottom padding), and the tile clips its own content, so it
   never reaches a neighbour tile or its PLAY. A locked tile has no PLAY. */
#hub .tile .why { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; color: var(--dim); line-height: 1.4; }
#hub .tile .why .fix {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0;
  font: inherit;
  letter-spacing: inherit;
  white-space: nowrap;
  color: var(--accent);
  background: none;
  border: 0;
  border-bottom: 1px dotted rgb(var(--accent-rgb) / 0.35);
  cursor: pointer;
}
#hub .tile .why .fix::after {
  content: "";
  position: absolute;
  left: -8px;
  right: -8px;
  bottom: -11px;
  height: 44px;
}
@media (hover: hover) {
  #hub .tile .why .fix:hover { border-bottom-color: var(--accent); }
}
#hub .tile .corner {
  position: absolute;
  right: 9px;
  top: 9px;
  padding: 3px 6px;
  border: 1px solid var(--edge);
  border-radius: 3px;
  background: rgb(var(--deep-rgb) / 0.72);
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 2px;
  color: var(--dim);
  isolation: isolate;
}
#hub .tile .corner::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: -10px -10px -14px -22px;
  background: radial-gradient(closest-side, rgb(var(--ink-rgb) / 0.6), rgb(var(--ink-rgb) / 0));
  pointer-events: none;
}
/* The `?` of each mode tile (R2445, R3178): it opens the HOW IT PLAYS
   card on the page of its mode. It stands at the top left, the
   corner tag keeps the top right, and `::after` gives it a finger's
   target inside the tile. The `.body` that follows it fills the tile, so
   the `?` takes z-index 1 to stay above it and take the press. */
#hub .tile .how {
  position: absolute;
  z-index: 1;
  left: 9px;
  top: 9px;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--edge);
  border-radius: 50%;
  background: rgb(var(--deep-rgb) / 0.72);
  color: var(--text);
  font: inherit;
  font-size: max(var(--type-floor), 11px);
  cursor: pointer;
}
#hub .tile .how::after { content: ''; position: absolute; inset: -9px; }
#hub .tile.core .how { color: var(--accent); }
#hub .tile.arcade .how { color: var(--amber); }
#hub .tile.event .how { color: var(--violet); }
#hub .tile.clanwar .how { color: var(--bad); }
@media (hover: hover) {
  #hub .tile .how:hover { background: rgb(var(--accent-rgb) / 0.18); }
}
#hub .tile .go {
  display: flex;
  align-items: center;
  min-height: 44px;
  margin-top: calc(max(var(--type-floor), 10px) * 1.4 - 44px);
  padding: 0 14px;
  border: 1px solid rgb(var(--accent-rgb) / 0.45);
  border-radius: 4px;
  background: rgb(var(--accent-rgb) / 0.14);
  color: var(--accent);
  font: inherit;
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 3px;
  cursor: pointer;
}
@media (hover: hover) {
  #hub .tile .go:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.24); }
}
#hub .tile.core .nm { color: var(--accent); }
#hub .tile.arcade .nm { color: var(--amber); }
#hub .tile.event .nm { color: var(--violet); }
#hub .tile.clanwar .nm { color: var(--bad); }
/* A locked tile fades its picture. The kicker, the name, the reason and
   the countdown stay readable: the kicker alone names the window, so it
   keeps the full Dim (R1734, R2045). The corner tag keeps its full ink
   too, so INCLUDED IN PREMIUM never reads as locked (R2151). */
#hub .tile.locked video.reel { filter: grayscale(1) brightness(0.6); }

/* --- forms, inputs and buttons ---------------------------------------- */

#hub input[type="text"],
#hub input[type="password"],
#hub input[type="email"],
#hub input[type="time"],
#hub select {
  font: inherit;
  min-height: 44px;
  padding: 0 10px;
  color: var(--text);
  background: rgb(var(--ink-rgb) / 0.7);
  border: 1px solid var(--edge);
  border-radius: 4px;
  letter-spacing: 1px;
}
/* A placeholder is the only word some fields show, so it takes Dim. */
#hub input::placeholder { color: var(--dim); letter-spacing: 2px; }
#hub input[type="range"] { width: 100%; accent-color: var(--accent); }

/* The look of a card's own button. A control of the choice kit (R2011),
   the volume pick of the chronicle book and the text button of a tile's
   refusal line keep their own look: `:not(:where(…))` skips them and adds
   no weight, so no later rule changes its place in the cascade. */
#hub .card button:not(:where(.vpick, .fix, [class^="ch-"])), #hub button.go {
  font: inherit;
  min-height: 44px;
  padding: 0 12px;
  color: var(--accent);
  background: rgb(var(--accent-rgb) / 0.12);
  border: 1px solid var(--edge);
  border-radius: 4px;
  letter-spacing: 2px;
  font-size: max(var(--type-floor), 10px);
  cursor: pointer;
}
@media (hover: hover) {
  #hub .card button:not(:where(.vpick, .fix, [class^="ch-"])):hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.22); }
}
/* A button that takes no press (R1751) keeps a label a player can read:
   Dim, with no ground, inside a dashed Faint edge, the look of a switch
   that takes no press (R1744). A fade of the whole button made FOUND IT
   almost invisible. */
#hub .card button:not(:where(.vpick, .fix, [class^="ch-"])):disabled,
#hub button.go:disabled {
  color: var(--dim);
  background: none;
  border-style: dashed;
  border-color: var(--faint);
  cursor: not-allowed;
}

/* --- the custom lobby (ui-map-interface.md §16.6, §16.17, R3088) ------- */

/* The EVENT tile carries HOST and the code box under its foot, so its row
   asks for the height of one more control row and one reason line. The
   tile clips its body, so a tile at the 150 px floor would cut them. */
#hub .tile.hosts { min-height: 212px; }
#hub .tile .cacts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
/* The foot rule of R2560 raises a CTA beside the name; a custom act
   stands in a row of its own and takes no such margin. */
#hub .tile .cacts .go { margin-top: 0; }
/* A live lobby leaves one press in the row, RESUME or RETURN TO LOBBY, and
   it stands at the right edge, where a held seat puts RESUME (R3168). */
#hub .tile .cacts.lone { justify-content: flex-end; }
#hub .tile .cjoin { display: flex; gap: 6px; margin-left: auto; }
#hub .tile .cwhy { margin-top: 4px; }
#hub input.ccode,
#hub input.cl-num,
#hub input.cl-text {
  font: inherit;
  min-height: 44px;
  padding: 0 10px;
  color: var(--text);
  background: rgb(var(--ink-rgb) / 0.7);
  border: 1px solid var(--edge);
  border-radius: 4px;
  letter-spacing: 1px;
}
/* Three symbols, read out loud: capitals, wide tracking, centred. */
#hub input.ccode { width: 6em; text-align: center; letter-spacing: 4px; text-transform: uppercase; }
#hub input.cl-num { width: 7em; text-align: right; }
/* The world name: the number boxes' look, wider, and set to the right like
   them. It shrinks on a telephone so the label keeps its room. */
#hub input.cl-text { flex: 0 1 14em; width: 14em; min-width: 8em; text-align: right; letter-spacing: 1px; }
#hub input.cl-num:disabled { color: var(--dim); border-style: dashed; border-color: var(--faint); }

/* The lobby page: the head, the code rows, two columns from 600 px (one on
   a telephone), and the foot of acts at the bottom edge of the pane, in
   the thumb zone (§12). */
#hub .cl { display: flex; flex-direction: column; gap: var(--hub-gap); }
#hub .cl-head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
#hub .cl-name {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 17px;
  font-weight: inherit;
  letter-spacing: 3px;
  color: var(--text);
}
#hub .cl-clock { margin-left: auto; font-size: max(var(--type-floor), 10px); letter-spacing: 1px; color: var(--accent); }
#hub .cl-codes .row { display: flex; align-items: center; gap: 8px; min-height: 44px; }
#hub .cl-codes .k,
#hub .cl .k { font-size: max(var(--type-floor), 9px); letter-spacing: var(--t-cap-track); color: var(--dim); }
#hub .cl-codes .k { min-width: 3.5em; }
#hub .cl-codes .code { font-size: 22px; letter-spacing: 6px; color: var(--accent); }
#hub .cl-codes .lnk {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: max(var(--type-floor), 10px);
  color: var(--text);
}
#hub .cl-codes .grow { flex: 1 1 0; }
#hub .cl-cols { display: grid; gap: var(--hub-gap); grid-template-columns: minmax(0, 1fr); align-items: start; }
@media (min-width: 600px) {
  #hub .cl-cols { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
#hub .cl-col { display: flex; flex-direction: column; gap: var(--hub-gap); min-width: 0; }
#hub .cl-fac + .cl-fac { margin-top: 12px; }
#hub .cl-fhead { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 4px; }
#hub .cl-person {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  border-bottom: 1px solid var(--edge);
}
#hub .cl-person:last-child { border-bottom: 0; }
#hub .cl-person .grow { flex: 1 1 0; }
#hub .cl-pn { display: flex; align-items: center; gap: 6px; min-width: 0; }
#hub .cl-pname { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text); }
#hub .cl-acts { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; }
#hub .cl-none,
#hub .cl-note,
#hub .cl-why,
#hub .cl-wall { margin: 4px 0; font-size: max(var(--type-floor), 10px); letter-spacing: 1px; line-height: 1.4; color: var(--dim); }
#hub .cl-why.bad { color: var(--bad); }
#hub .cl-facs { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 8px 0; }
#hub .cl-facs .cl-note { flex-basis: 100%; }
#hub .srow.locked .sn { color: var(--dim); }
#hub .cl-lock { color: var(--dim); }
#hub .cl-fig { color: var(--text); }
/* ADVANCED is a bar a player sees: a framed press with a chevron that
   turns when the block opens. The block opens under the bar. */
#hub .cl-adv { margin-top: 12px; }
#hub .cl-adv > summary {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 12px;
  font-size: max(var(--type-floor), 12px);
  letter-spacing: 3px;
  color: var(--accent);
  background: rgb(var(--accent-rgb) / 0.08);
  border: 1px solid rgb(var(--accent-rgb) / 0.45);
  border-radius: 4px;
  list-style: none;
  cursor: pointer;
}
#hub .cl-adv > summary::-webkit-details-marker { display: none; }
#hub .cl-adv > summary::before {
  content: "▸";
  font-size: 14px;
  transition: transform 0.15s ease;
}
#hub .cl-adv[open] > summary::before { transform: rotate(90deg); }
@media (hover: hover) {
  #hub .cl-adv > summary:hover { background: rgb(var(--accent-rgb) / 0.16); }
}
#hub .cl-adv[open] > summary { margin-bottom: 6px; }
@media (prefers-reduced-motion: reduce) {
  #hub .cl-adv > summary::before { transition: none; }
}
#hub .cl-flags { margin-top: 8px; }
#hub .cl-map { display: block; width: 100%; height: auto; border-radius: 6px; background: var(--console-ground); }
/* The foot is a framed bar. It stands 6 px clear of the pane edge, so the
   pane never cuts its lower border. */
#hub .cl-foot {
  position: sticky;
  bottom: 6px;
  z-index: 1;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  background: var(--console-ground);
  border: 1px solid var(--edge);
  border-radius: 6px;
}
#hub .cl-foot .hctl { margin: 0; justify-content: flex-end; }
#hub .cl-foot .cl-why { margin: 0 0 8px; }

/* Keyboard focus (R1693, ui-map-interface.md §8). The hub stands outside
   `#ui`, so it states the two focus looks of the chrome itself: the ring
   of `#ui button:focus-visible` on a control, and the outline of
   `#ui input:focus` on a field. A text field matches `:focus-visible` on
   a click as well, as a field must; a button does not. */
#hub button:focus-visible,
#hub a[href]:focus-visible,
#hub [tabindex="0"]:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
#hub input:focus-visible,
#hub select:focus-visible,
#hub textarea:focus-visible { outline: 1px solid rgb(var(--accent-rgb) / 0.5); }
/* The rename pencil paints 26 px and a volume pick 30 px, and a 44 px
   pseudo-element carries the target of each. Gecko draws an outline round
   the box and its positioned descendants, the target included, so these
   two take the ring as a box shadow, which stays on the painted box in
   every engine. The outline turns transparent and stays, because a
   forced-colours mode drops the shadow and paints the outline. */
#hub .top button.icon:focus-visible,
#hub button.vpick:focus-visible {
  outline-color: transparent;
  box-shadow: 0 0 0 2px var(--accent);
}
/* --- the achievement grid and the category rows (achievements.md §8,
   R937) ----------------------------------------------------------------

   The grid is a SHELF: one tile per category, the badge at the deepest
   grade the account holds in it, the name, the count and a bar. Two
   columns on a telephone, three in a column of a wide pane, and as many
   as fit across the whole width of RECORD — never one: a 360 px screen
   gives a two-column tile about 160 px, which is the badge at its own size
   with room for the name under it.

   The tile is the tap target, and the target is the whole tile: a badge is
   a picture and a picture the size of a fingertip is a control that misses.

   A row of a category page is a list line and not a shelf tile, so its
   badge is smaller and the words take the width. A row the account does
   not hold keeps its badge and dims it, because a ladder a player cannot
   see is a ladder nobody climbs. */
#hub .badgegrid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, 1fr);
}
@media (min-width: 600px) {
  #hub .badgegrid { grid-template-columns: repeat(3, 1fr); }
}
/* RECORD on a wide pane gives the card the whole width of the page, so the
   shelf takes as many columns of at least 150 px as fit. The 21 tiles then
   take three rows and not seven, and the first screen shows the head of
   every card above them (R1744). */
#hub .page > .card .badgegrid,
#hub .hs-box .hs-body > .card .badgegrid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
#hub .card .btile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  padding: 9px 5px 8px;
  color: var(--dim);
  background: rgb(var(--accent-rgb) / 0.04);
  border: 1px solid var(--edge);
  border-radius: 8px;
  letter-spacing: 0;
  font-size: max(var(--type-floor), 9px);
  text-align: center;
  cursor: pointer;
}
@media (hover: hover) {
  #hub .card .btile:hover {
    color: var(--accent);
    background: rgb(var(--accent-rgb) / 0.1);
    border-color: rgb(var(--accent-rgb) / 0.45);
  }
}
#hub .btile canvas { display: block; flex: none; }
#hub .btile .n {
  color: var(--text);
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 1.5px;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
#hub .btile .c { color: var(--dim); font-size: max(var(--type-floor), 9px); font-variant-numeric: tabular-nums; }
#hub .btile .bar {
  display: block;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: rgb(var(--faint-rgb) / 0.28);
  overflow: hidden;
}
#hub .btile .bar i { display: block; height: 100%; background: var(--accent); }

#hub .achrow {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08);
}
#hub .achrow:last-child { border-bottom: 0; }
#hub .achrow canvas { display: block; flex: none; opacity: 0.4; }
#hub .achrow.held canvas { opacity: 1; }
#hub .achrow .t {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  /* A list line reads from the left edge. It is stated rather than
     inherited, because the card above it centres a tile. */
  text-align: left;
}
#hub .achrow .t .n { color: var(--text); font-size: 11px; }
#hub .achrow .t .d { color: var(--dim); font-size: max(var(--type-floor), 10px); line-height: 1.4; }
#hub .achrow .t .f {
  color: var(--dim);
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 1px;
  font-variant-numeric: tabular-nums;
}
#hub .achrow .g {
  color: var(--dim);
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 2px;
  white-space: nowrap;
}
#hub .achrow.held .g { color: var(--accent); }
/* A row of a category page is a press: it picks the achievement for the
   YOUR PICK column of RECORD (§16.3). It keeps the look of a list line. */
#hub button.achrow {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  min-height: 44px;
  font: inherit;
  color: inherit;
  text-align: left;
  background: none;
  border: 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08);
  border-radius: 0;
  cursor: pointer;
}
#hub button.achrow.on { background: rgb(var(--accent-rgb) / 0.08); box-shadow: inset 3px 0 0 var(--accent); }
@media (hover: hover) {
  #hub button.achrow:hover:not(.on) { background: rgb(var(--accent-rgb) / 0.04); }
}

/* The companion of RECORD (§16.3): WITHIN REACH, YOUR PICK and VOLUMES.
   One line of a list holds the name and the figure, the category under
   them and the bar of the next step. */
#hub .reachrow { display: flex; flex-direction: column; gap: 3px; padding: 4px 0 8px; }
#hub .reachrow .rh { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; }
#hub .reachrow .rh canvas { display: block; flex: none; }
#hub .reachrow .rn { flex: 1; min-width: 0; color: var(--hero-ink); overflow-wrap: anywhere; }
#hub .reachrow .rh b { flex: none; font-weight: 500; color: var(--text); font-variant-numeric: tabular-nums; white-space: nowrap; }
#hub .reachrow .rc { color: var(--dim); font-size: max(var(--type-floor), 10px); letter-spacing: 0.12em; }
#hub :is(.reachrow, .hs-side, .card) > .bar {
  display: block;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: rgb(var(--accent-rgb) / 0.14);
  overflow: hidden;
}
#hub :is(.reachrow, .hs-side, .card) > .bar i { display: block; height: 100%; background: var(--accent); border-radius: 2px; }
#hub .pickhead { display: flex; align-items: center; gap: 10px; }
#hub .pickhead canvas { display: block; flex: none; }
#hub .pickhead .t { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
#hub .pickhead .n { color: var(--hero-ink); overflow-wrap: anywhere; }
#hub .pickhead .c { color: var(--dim); font-size: max(var(--type-floor), 10px); letter-spacing: 0.12em; }
/* The volume list of the chronicle (R1920): one row per volume on a pitch
   of 36 px. The box shows `record_volume_rows` rows and half of the next,
   so a longer list says that it scrolls, and the list never fills the
   pane. A press in the box acts on the release, so a scroll is no press
   (R1206). */
#hub .vollist {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  max-height: calc(6 * 36px + 15px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}
#hub .vollist .vrow { flex: none; justify-content: space-between; gap: 8px; text-align: left; white-space: nowrap; }
#hub :is(.vollist .vrow, .chronstep .cur) .vl { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
#hub :is(.vollist .vrow, .chronstep .cur) .vt { flex: none; letter-spacing: 1px; opacity: 0.75; }

/* The season chronicle (chronicle.md §9). One line of a page is a row of
   prose; a moment carries a mark and the accent. The closing line stands
   apart under a hairline. */
#hub .chron { display: flex; flex-direction: column; gap: 5px; text-align: left; }
#hub .chron .cs { margin-top: 6px; color: var(--dim); font-size: max(var(--type-floor), 9px); letter-spacing: 2px; }
#hub .chron .cs:first-child { margin-top: 0; }
#hub .cl { display: flex; flex-wrap: wrap; gap: 2px 6px; align-items: baseline; font-size: 11px; line-height: 1.5; color: var(--text); }
#hub .cl .mm { flex: none; color: var(--gold); font-size: max(var(--type-floor), 9px); }
#hub .cl .d { flex: none; color: var(--dim); font-size: max(var(--type-floor), 9px); letter-spacing: 1px; white-space: nowrap; }
#hub .cl .t { flex: 1 1 0; min-width: 12em; overflow-wrap: anywhere; }
#hub .cl.moment .t { color: var(--gold); }
#hub .cl.close { margin-top: 4px; padding-top: 6px; border-top: 1px solid rgb(var(--accent-rgb) / 0.12); color: var(--dim); }
#hub .chronshare { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
#hub .chronshare .tag { color: var(--dim); border-color: var(--edge); }
/* The book's bar stands at the foot of the pane, in the thumb zone of a
   telephone (ui-map-interface.md §12). It is one row at any count of
   volumes (R1920): ‹ older, the open volume with its place, › newer. A
   press on the open volume opens the volume list over the row. */
#hub .chronbar {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  background: var(--panel);
  border-top: 1px solid var(--edge);
}
#hub .chronstep { display: flex; align-items: center; gap: 6px; min-width: 0; }
#hub .chronstep .step { flex: none; justify-content: center; min-width: 44px; padding: 0; font-size: 12px; letter-spacing: 0; }
#hub .chronstep span.step { opacity: 0.35; }
#hub .chronstep .cur { flex: 1 1 auto; min-width: 0; justify-content: space-between; gap: 8px; white-space: nowrap; }
/* The REPLAY link of a battle line (chronicle.md §8) stands at the end of
   the line, and the COPY LINK box (§9.2) takes the whole row, so a thumb
   can select the address on a telephone. */
#hub .cl .rl { flex: none; margin-left: auto; text-decoration: none; }
#hub .chroncopy { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 8px; color: var(--dim); font-size: max(var(--type-floor), 10px); letter-spacing: 1px; }
#hub .chroncopy input { flex: 1 1 100%; min-width: 0; user-select: all; -webkit-user-select: all; }
/* The public reader (§9.4) is one pane and no header, dock or strip. */
/* THE SEA card of the public reader (chronicle.md §9.4, R1620, card 10 of
   mockup round 076). On a telephone the map takes the width of the column
   and the legend, the transport and the day marks stand under it; a wider
   pane puts the map beside them. The transport, the legend and the marks
   take the look of `styles.css` (ui-map-interface.md §3.6). ▸ ON THE MAP
   stands on the right of the head of each day page. */
#hub .card.tlread .tlb { flex-direction: column; }
#hub .card.tlread .tlmap { width: 100%; }
@media (min-width: 600px) {
  #hub .card.tlread .tlb { flex-direction: row; }
  #hub .card.tlread .tlmap { width: 230px; }
}
#hub .card .h .onmap { flex: none; }

#hub .form { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
#hub .form input { flex: 1 1 160px; min-width: 0; }
/* A box of a CLAN form with its visible label over it (R1738). A label
   names a control, so it takes Dim (R1739). */
#hub .form .field { display: flex; flex: 1 1 160px; flex-direction: column; gap: 4px; min-width: 0; }
#hub .form .field input { flex: none; width: 100%; }
#hub .form .field label { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; color: var(--dim); }
#hub .err { margin-top: 8px; font-size: 12px; color: var(--bad); line-height: 1.45; }

/* --- the settings row and its switch (DISCORD, CHAT RELAY, MODDING) -- */

#hub .srow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08);
}
#hub .srow:last-child { border-bottom: 0; }
#hub .srow .sk { flex: 1; min-width: 0; }
#hub .srow .sn { font-size: max(var(--type-floor), 10px); letter-spacing: 2px; color: var(--text); }
#hub .srow .sd { display: block; margin-top: 2px; font-size: 12px; color: var(--dim); line-height: 1.4; }
#hub .srow button.sw { flex: none; min-width: 66px; text-align: center; }
#hub .srow button.sw.off { color: var(--dim); background: rgb(var(--panel-rgb) / 0.6); }

/* --- the USE card of MODDING (themes.md §2.1, R815) -------------------- */

/* The theme preview stands in the PREVIEW companion of the section shell
   (R1902): the frame, then its caption. */
#hub .mpreview .mframe {
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 10;
  border: 1px solid var(--edge);
  border-radius: 4px;
  overflow: hidden;
  background: rgb(var(--deep-rgb));
}
#hub .mpreview .mframe > div { width: 100%; height: 100%; }
#hub .mpreview .sd { display: block; margin-top: 4px; font-size: 12px; color: var(--dim); line-height: 1.4; }
/* A row is a press target as a whole: the press selects it for the
   preview. The selected row wears the accent edge on its left. */
#hub .mrow { cursor: pointer; padding-left: 6px; }
#hub .mrow.sel { box-shadow: inset 2px 0 0 var(--accent); background: rgb(var(--accent-rgb) / 0.06); }
/* What one row opened: the code field, the overview, a note. It is inset
   like the sign-in form, so it reads as the row that opened. */
#hub .mdetail { margin: 2px 0 10px 6px; padding-left: 10px; border-left: 1px solid rgb(var(--accent-rgb) / 0.3); }
#hub .mdetail .row.press { cursor: pointer; }
#hub .mdetail .row.press .k { color: var(--dim); }
#hub .mdetail .row.sel .k { color: var(--accent); }
#hub .swatches { display: flex; flex-wrap: wrap; gap: 6px 10px; margin: 6px 0; }
#hub .swatch { display: inline-flex; align-items: center; gap: 4px; font-size: max(var(--type-floor), 9px); letter-spacing: 1px; color: var(--dim); }
#hub .swatch i { display: inline-block; width: 14px; height: 14px; border: 1px solid var(--edge); border-radius: 3px; }

/* --- the section shell: SETTINGS and the panels in its shape ---------- */

/* The section shell of the hub (ui-map-interface.md §16.16, `sectionshell.ts`)
   draws SETTINGS (R1731) and every panel that moved into its shape. The
   rail rows and the tiles are the kit of `ui/options/`: `options.css`
   holds their look, and `cards.css` the look of the hub cards in the kit.
   This block holds the layout of the shell inside the hub page alone: the
   box with the rail on a wide pane, the companion column beside it, and
   the tiles and the pages on a narrow one.

   SETTINGS is the whole box under the kit root `.opt` with the class
   `hset`, because its rows are kit rows. A panel of hub cards (`.grow`)
   puts the kit root on the rail and on the tiles alone, so its cards keep
   the look of the hub sheet.

   The kit names the text of a rail row and of the commander card `.t`.
   The tab rule of this sheet names the strip and the dock, so it does not
   reach that text, and the kit needs no reset of it here (R1739). */

/* A wide pane: one box of the rail and the pane, the look of the popup of
   card 20 inside the hub page. The box fills the pane, so a short section
   does not shrink it, and a long section makes it taller: the hub pane
   scrolls, and the rail stays at the top of the view. `overflow: clip`
   keeps the foot inside the round corners and makes no scroll box, which
   would stop the sticky rail. The box starts at the left edge of the pane,
   as every other section does, and it casts no shadow: it is a panel of
   the page and not a popup over it (R1744). */
/* The box grows to the foot of the pane, so it keeps the section gap above
   the legal row and its rule (R1751). */
/* The box stands in a row with the companion column. The SETTINGS box
   keeps its 980 px, because its rows are a form, and its WHERE IT SHOWS
   column (R1762) takes the strip to its right only while the row holds
   the box at 940 px, the gap and 190 px (R1910): the box yields from 980
   to 940 px, so a classic scrollbar does not take the column away at the
   common widths. */
/* The rules of the shell, of the back bar and of the uncharted empty state
   stand under `:is(#ui, #hub)` (R1929): the arcade lobby draws the shell
   in `#ui`, and the selector keeps the weight of one id. The game page
   loads this sheet too, and no element of its `#ui` wears these classes.
   `.card`, `.vpick`, `.row`, `.page` and `.h1` stay under `#hub`. */
#hub .paneinner > .page:has(> .hs-row) { flex: 1 1 auto; width: 100%; margin-bottom: var(--hub-gap); }
:is(#ui, #hub) .page > .hs-row {
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  gap: 20px;
  container: hs-row / inline-size;
}
:is(#ui, #hub) .hs-row > .hset { flex: 0 1 980px; min-width: 0; }
:is(#ui, #hub) .hs-row > .hs-where { flex: 1 0 190px; min-width: 0; max-width: 300px; }
@container hs-row (max-width: 1149px) {
  :is(#ui, #hub) .hs-row > .hs-where { display: none; }
}
:is(#ui, #hub) :is(.hset, .hs-box) {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  min-height: 340px;
  overflow: clip;
  border: 1px solid rgb(var(--accent-rgb) / 0.35);
  border-radius: 10px;
  background: rgb(var(--panel-rgb) / 0.96);
}
:is(#ui, #hub) .hs-rail {
  position: sticky;
  top: 0;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px;
}
/* The rail ground runs down the whole box, whatever the rail holds. */
:is(#ui, #hub) :is(.hset, .hs-box)::before {
  content: "";
  grid-column: 1;
  grid-row: 1;
  background: rgb(var(--deep-rgb) / 0.45);
  border-right: 1px solid var(--edge);
}
:is(#ui, #hub) .hs-rail, :is(#ui, #hub) .hs-pane { grid-row: 1; }
:is(#ui, #hub) .hs-rail { grid-column: 1; }
:is(#ui, #hub) .hs-pane { grid-column: 2; display: flex; flex-direction: column; min-width: 0; }
:is(#ui, #hub) .hs-sep { height: 1px; margin: 8px 4px; background: var(--edge); }
:is(#ui, #hub) .hs-body { flex: 1 0 auto; padding: 4px 28px 18px; }

/* A panel of hub cards: the box takes the pane and the column takes a
   fixed width (§16.16). The page title names the section and its
   summary. The pane holds one column of blocks, and a page puts its cards
   in the grid (`cardGrid`), whose columns are 330 px at least, so a
   wide screen gets one more column of cards. */
:is(#ui, #hub) .hs-row > .hs-box.grow { flex: 1 1 auto; min-width: 0; }
:is(#ui, #hub) .hs-box.grow .hs-body {
  display: flex;
  flex-direction: column;
  gap: var(--hub-gap);
  padding: 14px 22px 18px;
}
/* The page title of a section (R1921) is the kit title of SETTINGS
   (`options.css` "the page title"): the glyph box, the word and the
   summary. The kit root wraps the title alone, so the cards keep the hub
   sheet. The title starts where the SETTINGS title starts, and a hairline
   under it sets it off from the cards. A narrow page has the pane gap over
   its big title, where SETTINGS has the margin of the title. */
:is(#ui, #hub) .opt.hs-ptitle { border-bottom: 1px solid rgb(var(--accent-rgb) / 0.1); }
:is(#ui, #hub) .opt.hs-ptitle:not(:has(.o-pw)) { padding-bottom: 8px; }
:is(#ui, #hub) .opt.hs-ptitle > .o-pt { margin-top: 8px; }
:is(#ui, #hub) .opt.hs-ptitle > .o-pt.big { margin-top: 0; }
:is(#ui, #hub) .hs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(330px, 100%), 1fr));
  gap: var(--hub-gap);
  align-items: start;
}
/* A sub-page inside the pane (a category of achievements): ‹ SECTION in
   the back form of R1751 takes the place of the title line. */
:is(#ui, #hub) .hs-box.grow .hs-body > .hback:first-child { margin-top: -8px; }

/* The companion column (§16.16): one object the panel works on, beside the
   box. It stands only while the row holds the box at its minimum, the gap
   and the column, a container query on the row. The rule is one for every
   panel, and the room stays under the row of the capped hub by a margin
   (R1910). Under that room the column
   goes, and the content folds where the panel says: a band over the page,
   a pinned foot of the page, a block after the page, or a rail row of its
   own (`.hs-compact`), which the rail shows only while the column cannot
   stand. A wide row gives the column its wide size. `.large` is the
   column of a chat. */
:is(#ui, #hub) .hs-row.grow { gap: 20px; }
:is(#ui, #hub) .hs-row.grow > .hs-side {
  display: none;
  flex: 0 0 280px;
  flex-direction: column;
  gap: 9px;
  min-width: 0;
  align-self: start;
  position: sticky;
  top: 0;
  padding: 12px 14px;
  border: 1px solid rgb(var(--accent-rgb) / 0.16);
  border-radius: 10px;
  background: rgb(var(--deep-rgb) / 0.4);
}
:is(#ui, #hub) .hs-row.grow.large > .hs-side { flex-basis: 300px; }
:is(#ui, #hub) .hs-side > .hs-lab {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 0.14em;
  color: var(--dim);
}
@container hs-row (min-width: 1140px) {
  :is(#ui, #hub) .hs-row.grow:not(.large) > .hs-side { display: flex; }
  :is(#ui, #hub) .hs-row.grow:not(.large) :is(.hs-fold, .hs-compact) { display: none; }
}
@container hs-row (min-width: 1160px) {
  :is(#ui, #hub) .hs-row.grow.large > .hs-side { display: flex; }
  :is(#ui, #hub) .hs-row.grow.large :is(.hs-fold, .hs-compact) { display: none; }
}
/* The pinned foot of a page: it stands at the foot of the view while the
   page scrolls under it. */
:is(#ui, #hub) .hs-pfoot {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 8px 22px;
  border-top: 1px solid var(--edge);
  background: rgb(var(--ink-rgb) / 0.97);
}
:is(#ui, #hub) .hs-page.grow .hs-pfoot { margin: 0 -14px -14px; padding: 8px 14px; }

/* A narrow pane: the tiles, then one page a section under ‹ PANEL. The
   head stays at the top of the pane while the page scrolls; it takes back
   the padding of the pane, so its rule runs from edge to edge. */
:is(#ui, #hub) .opt.hs-root { container: hs-root / inline-size; }
:is(#ui, #hub) .opt.hs-root .o-lab:first-child { margin-top: 2px; }
/* A UI scale of 120 % or 140 % leaves a tile too narrow for its word: the
   tile first gives up its chevron, then its summary line, the way the
   telephone root of card 20 does at 140 %. */
@container hs-root (max-width: 340px) {
  :is(#ui, #hub) .opt.hs-root .o-st .o-chev { display: none; }
  :is(#ui, #hub) .opt.hs-root .o-st { padding-right: 10px; }
}
@container hs-root (max-width: 280px) {
  :is(#ui, #hub) .opt.hs-root .o-sg { gap: 6px; }
  :is(#ui, #hub) .opt.hs-root .o-st { min-height: 46px; padding: 6px 8px; gap: 8px; }
  :is(#ui, #hub) .opt.hs-root .o-stg { width: 26px; height: 26px; font-size: 13px; }
  :is(#ui, #hub) .opt.hs-root .o-stt b { font-size: max(var(--type-floor), 10.5px); letter-spacing: 0.08em; }
  :is(#ui, #hub) .opt.hs-root .o-stt i { display: none; }
}
/* The tile word of a panel of hub cards wraps to a second line and never
   clips, as the strip rule of R1741 says; SETTINGS keeps the clip of the
   kit. */
:is(#ui, #hub) .opt.hs-root.wrap .o-stt b { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: break-word; }
:is(#ui, #hub) .opt.hs-root .o-who {
  min-height: 56px;
  padding: 8px 10px;
  border: 1px solid var(--edge);
  border-radius: 10px;
  background: rgb(var(--accent-rgb) / 0.045);
}
:is(#ui, #hub) .opt.phone .o-head.hs-head {
  position: sticky;
  top: -14px;
  z-index: 3;
  min-height: 52px;
  margin: -14px -14px 0;
  padding: 0 6px 0 2px;
  background: rgb(var(--ink-rgb));
}
:is(#ui, #hub) .hs-page .hs-body { padding: 0 2px 12px; }
/* The page of a panel of hub cards: ‹ PANEL in the back form of R1751, in
   the place of the SETTINGS head and with no section word, as that head
   has none: the big page title names the section (R1921). A sub-page
   keeps ‹ SECTION and its own word in Dim (R1780). */
:is(#ui, #hub) .hs-page.grow { display: flex; flex-direction: column; gap: var(--hub-gap); }
:is(#ui, #hub) .hs-page.grow > .hback:first-child {
  position: sticky;
  top: -14px;
  z-index: 3;
  gap: 10px;
  min-height: 52px;
  margin: -14px -14px 0;
  padding: 0 14px;
  background: rgb(var(--ink-rgb));
}
:is(#ui, #hub) .hs-page.grow > .hs-body { display: flex; flex-direction: column; gap: var(--hub-gap); padding: 0 0 12px; }

/* --- the clan heading and the roster controls (CLAN) ------------------- */

/* The heading of a clan's own screen (R1562). It takes two lines at every
   width: the section name, the clan name and the tag on the first, the
   member count and the banner the clan flies on the second (R257). The
   name is the one item of the first line that shrinks. It clips with an
   ellipsis, so a long name never wraps the line and never pushes the tag
   off it. The second line may wrap only at a width no telephone has. The
   banner pill is a label and no control, so it takes a lower box. */
#hub .clanhead { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
#hub .clanhead .l1 { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
#hub .clanhead .lb, #hub .clanhead .tg { flex: none; white-space: nowrap; }
#hub .clanhead .nm {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  letter-spacing: 2px;
}
#hub .clanhead .tg { font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--dim); }
#hub .clanhead .l2 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  min-width: 0;
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 2px;
  color: var(--dim);
}
#hub .clanhead .ct { white-space: nowrap; }

/* The controls of one open roster row (R257). They are three runs in a
   fixed order — the squad moves, the role, then the destructive pair — so
   the same control sits under the same part of the row on every member. A
   loose cloud of controls moved REMOVE with every label beside it. */
#hub .editrow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 2px 0 10px;
  padding-top: 8px;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.12);
}
#hub .editrow .grp { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
#hub .editrow .grp + .grp {
  padding-left: 10px;
  border-left: 1px solid rgb(var(--accent-rgb) / 0.14);
}
/* The moves are one run of equal cells, so the run is the same width on
   every row and the run beside it always starts in the same place. */
#hub .editrow .grp.moves .ch-btn { min-width: 44px; padding: 0 8px; }
/* The destructive pair holds the right end of the row, away from the move a
   press is meant to land on. */
#hub .editrow .grp.gone { margin-left: auto; }

/* --- the clan cup (CLAN, clan-cup.md §8, mockup round 078 cards 06-10) -- */

/* The sub-headers of the cup of now and of the CUP BRACKET wear the
   arcade's amber, so a cup never reads as a Clan War (card 06). They draw
   no box (R1945), so the amber moves from the edge to the name. */
#hub .card.sect.cupc > .o-sec b { color: rgb(var(--amber-rgb)); }
#hub .note2 { margin-top: 6px; font-size: 12px; color: var(--dim); line-height: 1.5; }
#hub .cupfoot { display: flex; align-items: center; gap: 8px; }
#hub .cupfoot .note2 { flex: 1; margin-top: 0; }
/* One amber line says why a cup is cancelled, and that nobody loses
   anything. */
#hub .cupcancel {
  margin-bottom: 8px;
  padding: 8px 10px;
  border: 1px solid rgb(var(--amber-rgb) / 0.45);
  border-radius: 6px;
  background: rgb(var(--amber-rgb) / 0.06);
  color: var(--amber);
  font-size: max(var(--type-floor), 10.5px);
  letter-spacing: 1px;
  line-height: 1.5;
}
#hub .cupcancel b { color: var(--text); font-weight: 400; }
/* The cup title under the clan name (card 10). */
#hub .cupline { display: flex; align-items: center; gap: 7px; font-size: max(var(--type-floor), 10px); letter-spacing: 2px; color: var(--gold); }
#hub .nownow { margin-bottom: 4px; font-size: 15px; letter-spacing: 3px; color: var(--good); }

/* The cup mark beside a tag (card 09): the height of the tag, in gold. A
   press names the cup beside it. */
#hub .cupm, #hub .bigcup { display: inline-flex; align-items: center; color: var(--gold); vertical-align: -2px; margin-left: 5px; }
#hub .cupm.tip::after { content: attr(data-cup); margin-left: 5px; font-size: max(var(--type-floor), 9px); letter-spacing: 1px; white-space: nowrap; }
#hub .row .v .cupm { margin: 0 5px 0 0; }
/* The heading aligns its line on the baseline; the mark stands on the
   middle of the tag instead. */
#hub .clanhead .l1 .cupm { align-self: center; margin-left: 0; }

/* The cup bracket (card 07): four columns on a wide pane, the rounds from
   left to right and the cup at the end of the line; the rounds stacked in
   time order on a narrow one. */
#hub .brk { font-size: max(var(--type-floor), 10.5px); }
#hub .brk.cols { display: grid; grid-template-columns: repeat(var(--rounds, 3), minmax(0, 1.1fr)) minmax(0, 0.7fr); gap: 0 18px; }
#hub .brk .col { display: flex; flex-direction: column; min-width: 0; }
#hub .brk .col .ms { flex: 1; display: flex; flex-direction: column; justify-content: space-around; }
#hub .brk .rnd { padding-bottom: 6px; font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--dim); text-align: center; }
#hub .brk.stack .rnd { margin-top: 6px; text-align: left; }
#hub .brk .m { position: relative; margin: 5px 0; border: 1px solid var(--edge); border-radius: 5px; background: rgb(var(--accent-rgb) / 0.03); }
#hub .brk .m.live { border-color: rgb(var(--good-rgb) / 0.6); box-shadow: 0 0 0 2px rgb(var(--good-rgb) / 0.12); }
#hub .brk .m.me { border-color: rgb(var(--accent-rgb) / 0.6); }
#hub .brk .s { display: flex; align-items: center; gap: 6px; padding: 3px 7px; white-space: nowrap; }
#hub .brk .s + .s { border-top: 1px solid rgb(var(--accent-rgb) / 0.08); }
#hub .brk .s .sd { width: 1.4ch; color: var(--dim); font-size: max(var(--type-floor), 9px); text-align: right; }
#hub .brk .s .cn { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; color: var(--text); }
#hub .brk .s .cn i { font-style: normal; font-size: max(var(--type-floor), 9px); letter-spacing: 1px; color: var(--bad); }
#hub .brk .s .cn.tbd { color: var(--faint); }
#hub .brk .s .sc { color: var(--text); font-variant-numeric: tabular-nums; }
#hub .brk .s.w .cn { color: var(--text); }
#hub .brk .s.l .cn, #hub .brk .s.l .sc { color: var(--faint); }
#hub .brk .s.me .cn { color: var(--accent); }
#hub .brk .s.bye .cn { color: var(--faint); font-style: italic; }
#hub .brk .tag2 { position: absolute; top: -8px; right: 6px; padding: 0 5px; border-radius: 3px; font-size: max(var(--type-floor), 9px); letter-spacing: 1.5px; }
#hub .brk .tag2.live { background: var(--good); color: rgb(var(--ink-rgb)); }
#hub .brk .tag2.when { top: -9px; color: var(--dim); }
#hub .brk .cupcol { text-align: center; }
#hub .brk .bigcup { flex-direction: column; margin: 0; }
#hub .brk .bigcup.faint svg { opacity: 0.35; }
#hub .brk .cupyear { margin-top: 4px; font-size: max(var(--type-floor), 9px); letter-spacing: 2px; color: var(--dim); }

/* The line of times of the cup day and the strip of cup Saturdays
   (card 08). */
#hub .tline { position: relative; padding-left: 16px; }
#hub .tline::before { content: ''; position: absolute; left: 5px; top: 4px; bottom: 4px; width: 1px; background: var(--edge); }
#hub .tline .ev { position: relative; padding: 4px 0 6px; }
#hub .tline .ev::before {
  content: ''; position: absolute; left: -15px; top: 8px; width: 9px; height: 9px;
  border: 1px solid var(--dim); border-radius: 50%; background: rgb(var(--ink-rgb));
}
#hub .tline .ev.done::before { background: var(--dim); }
#hub .tline .ev.now::before { border-color: var(--good); background: var(--good); box-shadow: 0 0 0 3px rgb(var(--good-rgb) / 0.2); }
#hub .tline .ev.me::before { border-color: var(--accent); background: var(--accent); }
#hub .tline .ev .tlt { margin-right: 8px; font-size: 11px; color: var(--text); font-variant-numeric: tabular-nums; }
#hub .tline .ev .tlx { font-size: 11px; color: var(--text); }
#hub .tline .ev .tly { display: block; font-size: max(var(--type-floor), 9.5px); letter-spacing: 0.5px; color: var(--dim); }
#hub .tline .ev.me .tlx { color: var(--accent); }
#hub .tline.off .ev .tlt, #hub .tline.off .ev .tlx { color: var(--faint); text-decoration: line-through; }
#hub .kick { margin: 10px 0 6px; font-size: max(var(--type-floor), 9px); letter-spacing: var(--t-cap-track); color: var(--dim); }
#hub .cal { display: flex; flex-wrap: wrap; gap: 6px; }
#hub .cal span {
  flex: 1 1 60px; padding: 5px 6px; border: 1px solid var(--edge); border-radius: 5px;
  font-size: max(var(--type-floor), 9px); letter-spacing: 1px; line-height: 1.4; color: var(--dim); text-align: center;
}
#hub .cal span b { display: block; font-size: 13px; font-weight: 400; letter-spacing: 1px; color: var(--text); }
#hub .cal span.next { border-color: rgb(var(--accent-rgb) / 0.55); color: var(--accent); }
#hub .cal span.next b { color: var(--accent); }
#hub .cal span.off { border-style: dashed; color: var(--faint); }
#hub .cal span.off b { color: var(--faint); text-decoration: line-through; }

/* --- the chat (CLAN) --------------------------------------------------- */

#hub .chat { display: flex; flex-direction: column; gap: 6px; max-height: 260px; overflow-y: auto; }
#hub .msg { display: flex; gap: 8px; align-items: baseline; font-size: 11px; }
/* The chat time stamp `[HH:MM]` of the SOCIAL switch (R1791). */
#hub .msg .ts { color: var(--dim); font-variant-numeric: tabular-nums; white-space: nowrap; }
#hub .msg .who { color: var(--accent); white-space: nowrap; }
#hub .msg .who.me { color: var(--gold); }
#hub .msg .b { min-width: 0; color: var(--dim); word-break: break-word; }
#hub .msg .t { margin-left: auto; font-size: max(var(--type-floor), 9px); color: var(--dim); white-space: nowrap; }
/* The control that opens REPORT and BLOCK under another member's line,
   and the inline report form (deployment.md §7.6). */
#hub .msg .ch-btn { flex: none; }
#hub .reportform { margin: 4px 0 8px; padding: 8px; border: 1px solid rgb(var(--bad-rgb) / 0.4); }
#hub .reportform .form { margin-top: 6px; }
#hub .chatmark {
  padding: 4px 0;
  border-top: 1px dashed rgb(var(--gold-rgb) / 0.4);
  border-bottom: 1px dashed rgb(var(--gold-rgb) / 0.4);
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 2px;
  color: var(--gold);
  text-align: center;
}
#hub .chatbox { display: flex; gap: 6px; margin-top: 8px; }
#hub .chatbox input { flex: 1; min-width: 0; }

/* --- the footer, which carries the three legal links ------------------ */

/* The legal row stands at the foot of the pane's content and scrolls with
   it (R1741), so a telephone does not lose 41 px of every section to it.
   `margin-top: auto` keeps it at the bottom of a short section. It keeps
   16 px under its links, because the dock can stand right under it: a
   press that misses a dock tab upward lands on that padding and opens no
   legal page (R1736). */
#hub .paneinner > .foot {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin-top: auto;
  padding: 14px 0 16px;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.1);
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 2px;
}
#hub .foot a { color: var(--dim); text-decoration: none; }
@media (hover: hover) {
  #hub .foot a:hover { color: var(--accent); }
}
/* The privacy notice and the terms have a long name and a short one
   (R732). The narrow block below swaps them for PRIVACY and TERMS. */
#hub .foot .narrow { display: none; }

/* --- narrow screens ---------------------------------------------------- */

@media (max-width: 900px) {
  #hub .foot .wide { display: none; }
  #hub .foot .narrow { display: inline; }
  /* The six short names needed 372 px at the wide spacing, and a 393 px
     screen gave the row 365 px, so the row broke into two lines on a
     telephone (R747). The narrow gap and the narrow padding put the need
     at 332 px and the room at 373 px. The row now holds five names, so
     the need is less (R1736). The 16 px under the links stays. */
  #hub .paneinner > .foot { gap: 4px 10px; }
  #hub .split, #hub .cdsplit { grid-template-columns: 1fr; }
  #hub .top .wm { display: none; }
  /* The environment tag keeps the stage cell alone, so the player name
     keeps its room (R2872). */
  #hub .top .envtag .env { display: none; }
  #hub .top .envtag .stage { border-left: 0; padding: 0 5px; }
  /* The player name takes the room of the header (R1739). The badge
     takes its content width, and the badge and the sign-out control give
     back tracking and padding, so a name of 12 characters shows whole on
     a 390 px screen at the 11 px floor of a finger. */
  #hub .top { gap: 8px; }
  #hub .tierbadge { min-width: 0; padding: 0 6px; letter-spacing: 1px; }
  #hub .top button:not(.icon) { padding: 0 6px; letter-spacing: 1px; }
  /* A guest's tier badge gives its room to the name, so a guest name
     beside its faction badge shows whole at 390 px, at rest and under
     LOSE CAPTAINS AND HONOUR?. While the sign-out press is armed, every
     account's badge gives its room to the name (R2208). */
  #hub .tierbadge.guest,
  #hub .top:has(> button.armed) .tierbadge { display: none; }
  /* Five names in a row on a 360 px screen. WARDROOM is the longest of
     them, so the dock gives back one pixel of letter-spacing and one of
     size rather than clip a name. */
  #hub .dock .t { font-size: 11px; letter-spacing: 1px; padding: 0 2px; }
  #hub .strip .t { font-size: max(var(--type-floor), 10px); letter-spacing: 1px; padding: 0 2px; }
  /* Under a coarse pointer the floor is 11 px, and EINSTELLUNGEN wraps
     to two lines in its fifth of 390 px (R1741). */
  /* The MODDING preview stays small on a telephone, so the six rows are
     one short scroll away (R815). */
  #hub .mpreview .mframe { aspect-ratio: 16 / 9; max-height: calc(30vh / var(--ui-scale, 1)); }
  /* A MODDING row on a telephone: the name and the stepper share the first
     line, and the description takes the full width of the second, so it
     reads in one or two lines and not in four (R818). The value keeps its
     desktop width, so the arrows stay put here too. */
  #hub .mrow { display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 10px; }
  #hub .mrow .sk { display: contents; }
  #hub .mrow .sn { grid-column: 1; grid-row: 1; align-self: center; }
  #hub .mrow .sd { grid-column: 1 / -1; grid-row: 2; }
}

/* --- a finger: the type floor and the targets (R1739) ----------------- */

/* A coarse pointer raises the type floor to 11 px, so every size under
   11 px of this sheet and of the SETTINGS kit reads 11 px (DESIGN.md
   "Typography"). Where a word must still fit its box, the tracking gives
   way first, as the narrow block does for the tab names. */
@media (pointer: coarse) {
  #hub { --type-floor: 11px; }
  /* A press that paints narrower than a finger takes 44 px across. */
  #hub button.vpick { min-width: 44px; justify-content: center; }
  #hub .mdetail .row.press { min-height: 44px; align-items: center; }
  /* WEITERSPIELEN is the longest call to action (R1752). The foot grid
     gives the state line the room the button leaves (R1741). */
  #hub .tile .go { letter-spacing: 2px; }
}

/* --- the desktop frame ------------------------------------------------
   A menu that fills a 27-inch screen puts its dock a whole arm away from
   its header. On a wide screen the shell therefore stops growing: it caps
   at 1280x900, centres itself, and keeps a gutter on every side. The
   surround goes one step deeper than the sheet, so the gutter reads as
   room around a console and not as an unpainted edge.

   The cap is the width every mockup of rounds fourteen to sixteen was
   drawn at, so what ships is what was judged.

   A telephone keeps the full bleed. The break is 901 px, which is the
   other side of the `max-width: 900px` rule above. */
@media (min-width: 901px) {
  #hub {
    margin: auto;
    max-width: min(1280px, calc(100vw / var(--ui-scale, 1) - 48px));
    max-height: min(var(--wide-px), calc(100vh / var(--ui-scale, 1) - 48px));
    border: 1px solid var(--edge);
    border-radius: 12px;
  }
}

/* --- MODDING in the section shell (R1902, §16.3, §16.16) --------------
   The PREVIEW companion: the theme preview and its caption. In the column
   the frame takes the width and the caption stands under it. The band of
   a wide pane with no room for the column puts the frame of 240 px beside
   the caption. A telephone pins the band under ‹ MODDING (whose head
   shows 38 px of its 52 px while the pane scrolls), with no caption, so
   the rows scroll under the picture they change. */
#hub .mpreview .mcap { display: flex; flex-direction: column; gap: 2px; margin-top: 8px; }
#hub .mpreview .mcap b {
  font-weight: 500;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 0.12em;
  color: var(--hero-ink);
}
#hub .mpreview .mcap .sd { margin-top: 0; }
#hub .hs-box .hs-fold .mpreview {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}
#hub .hs-box .hs-fold .mpreview .mcap { margin-top: 0; }
#hub .hs-page .hs-fold:has(.mpreview) {
  position: sticky;
  top: 38px;
  z-index: 2;
  background: rgb(var(--ink-rgb));
}
#hub .hs-page .hs-fold .mpreview .mcap { display: none; }
/* --- end of MODDING ---------------------------------------------------- */

/* --- RANKS in the section shell (R1903) --------------------------------
   The companion column YOU ON THIS BOARD of RANKS (ui-map-interface.md
   §16.3, card 02 of mockup round 092): the reader's place in the large
   face over the value, the places on each side of the reader's row, and
   the pinned foot that scrolls the board to that row. */
#hub .ybig { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; margin-bottom: 8px; }
#hub .ybig b {
  font-size: 26px; font-weight: 300; letter-spacing: 2px; line-height: 1.1;
  color: var(--hero-ink); font-variant-numeric: tabular-nums;
}
#hub .ybig small { font-size: max(var(--type-floor), 10px); letter-spacing: 1.5px; color: var(--dim); }
#hub .ynear {
  display: grid; grid-template-columns: 5.5ch 1fr auto; gap: 0 9px; align-items: center;
  padding: 4px 6px; border-bottom: 1px solid rgb(var(--accent-rgb) / 0.07); font-size: 11px;
}
#hub .ynear .n { color: var(--dim); text-align: right; font-variant-numeric: tabular-nums; }
#hub .ynear .nm { min-width: 0; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#hub .ynear .v { color: var(--dim); font-variant-numeric: tabular-nums; white-space: nowrap; }
/* The reader's own row: a tint and Hero Ink, and no side stripe. The
   stripe is the one selection mark of the kit (R1744), and this row is
   no selection (R2152). */
#hub .ynear.me { background: rgb(var(--accent-rgb) / 0.1); }
#hub .ynear.me :is(.n, .nm, .v) { color: var(--hero-ink); }
#hub .ynear + .row { margin-top: 8px; }
/* The pinned foot is one press: the whole row is the target (§16.8), so it
   is at least 44 px high. */
#hub .ypin {
  display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px;
  padding: 0; border: 0; background: none; font: inherit; color: var(--text); text-align: left; cursor: pointer;
}
#hub .ypin .k { flex: none; font-size: max(var(--type-floor), 10px); letter-spacing: 2px; color: var(--dim); }
#hub .ypin b { flex: 1; min-width: 0; font-weight: 500; color: var(--hero-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#hub .ypin .a { flex: none; font-size: max(var(--type-floor), 10px); letter-spacing: 1.5px; color: var(--accent); }

/* --- CLAN in the section shell (ui-map-interface.md §16.11) ----------- */

/* The chat is the companion column of every page of a clan (card 04 of
   mockup round 092). It stands by the one room rule of the shell, from a
   row of 1160 px (R1910); under it the CHAT row of the rail is the
   fold. */
/* The column holds the log and the box with no card round them. The log
   takes the height of the view less the heads of the hub, so the box
   stands near the foot of the view. */
#hub .hs-side .clanchat { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
#hub .hs-side .clanchat > .hctl { margin: 0; }
#hub .hs-side .clanchat .chat {
  min-height: 160px;
  max-height: max(160px, calc(100vh / var(--ui-scale, 1) - 340px));
}

/* --- WARDROOM in the section shell (R1904, ui-map-interface.md §16.9) ---
   The toggle row opens the ROSTERS page, so it draws no rule over it. A
   roster grid of the page takes columns of at least 104 px: a wide pane
   shows six tiles in a row, and a telephone three. */
#hub .wd-roster .caps { grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); }
/* A portrait of the dossier, the preview and the strip: the panel states
   the size as `--wd-px` from [presentation.hub_sections] and
   [presentation.portrait]. The gradient is the placeholder circle until
   the render is ready. */
#hub :is(.wd-face, .wd-sf) {
  flex: none;
  width: var(--wd-px);
  height: var(--wd-px);
  border: 1px solid var(--edge);
  border-radius: 50%;
  overflow: hidden;
  background: radial-gradient(circle at 40% 35%, rgb(var(--accent-rgb) / 0.35), rgb(var(--panel-rgb) / 0.9));
}
#hub :is(.wd-face, .wd-sf) > .p { width: 100%; height: 100%; }
#hub :is(.wd-face, .wd-sf) img { display: block; width: 100%; height: 100%; border-radius: 50%; }
/* The DOSSIER (card 05 of round 092, R1118): the head, then one row an
   ability: the short name, the bar to the training cap and the points. A
   talent is the hero ink with an underline, as the captain row draws it
   (R1889). */
#hub .wd-dhead { display: flex; align-items: center; gap: 12px; }
#hub .wd-dt { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
#hub :is(.wd-dt, .wd-prev) b { font-weight: 500; color: var(--hero-ink); overflow-wrap: anywhere; }
#hub :is(.wd-dt, .wd-prev) span { color: var(--dim); font-size: max(var(--type-floor), 10px); letter-spacing: 0.12em; }
#hub .wd-dt .wd-tal { color: var(--accent); }
#hub .wd-abil {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px 10px;
  margin-top: 6px;
  font-size: max(var(--type-floor), 10px);
}
#hub .wd-an { color: var(--dim); letter-spacing: 0.12em; }
#hub .wd-an.t { color: var(--hero-ink); text-decoration: underline; text-underline-offset: 3px; }
#hub .wd-av { color: var(--text); text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
#hub .wd-bar { display: block; height: 4px; border-radius: 2px; background: rgb(var(--accent-rgb) / 0.14); overflow: hidden; }
#hub .wd-bar i { display: block; height: 100%; background: var(--accent); border-radius: 2px; }
/* The strip of portraits over the tiles of a telephone and over the form
   of the Captain Designer. The padding makes a press of 44 px round a
   portrait of 40 px. */
#hub .wd-strip { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 2px 0 6px; }
#hub .wd-sf { box-sizing: content-box; padding: 2px; background-clip: content-box; }
#hub .wd-sf[role="button"] { cursor: pointer; }
#hub .wd-sf.on { border-color: var(--accent); }
#hub .wd-more { color: var(--dim); font-size: max(var(--type-floor), 11px); letter-spacing: 0.08em; }
/* The live preview of the Captain Designer, and the hull of FLEET
   LIVERY: centred, the whole width of the column, and at most 160 px wide
   in a fold (`short`), so the grid of a telephone stays near the top
   (R2186). */
#hub .wd-prev { display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center; }
#hub .wd-prev .wd-face { margin-bottom: 6px; }
#hub .wd-hull { display: block; width: 100%; height: auto; margin: 0 auto; border-radius: 6px; }
#hub .wd-hull.short { max-width: 160px; }
/* The naming diagram of FLEET NAMES (R2186, `fleetdiagram.ts`): row 1 is
   the example name in four parts, each part over a bracket, with no gap
   between the faction letter and the role, as the server writes them.
   Under it a stem runs down from the left of each part to its label, the
   rightmost part on the top row, so no stem crosses a label. The last
   column keeps room for the label of the ordinal. */
#hub .fd-dg {
  display: grid;
  grid-template-columns: auto auto minmax(0, max-content) auto minmax(72px, 1fr);
  margin: 4px 0 6px;
}
#hub .fd-dw {
  grid-row: 1;
  min-width: 0;
  margin-right: 2px;
  padding: 0 1px 3px;
  border-bottom: 2px solid var(--accent);
  font-size: max(var(--type-floor), 20px);
  font-weight: 500;
  line-height: 1.2;
  color: var(--hero-ink);
  overflow-wrap: anywhere;
}
#hub .fd-dw.fac { color: var(--fd-ink, var(--hero-ink)); border-bottom-color: var(--fd-ink, var(--accent)); }
#hub .fd-dw:is(.nm, .ord) { margin-left: 11px; }
#hub .fd-dw.ord { color: var(--dim); border-bottom-style: dashed; border-bottom-color: var(--dim); }
/* A stem and the corner of its label start 5 px into the part, at the
   middle of a 20 px letter; a name and an ordinal start after the 11 px
   space. */
#hub :is(.fd-ds, .fd-dl) { margin-left: 5px; }
#hub :is(.fd-ds, .fd-dl):is(.nm, .ord) { margin-left: 16px; }
#hub .fd-ds { border-left: 1.5px solid rgb(var(--accent-rgb) / 0.5); }
#hub .fd-dl {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  padding: 5px 0 0 12px;
}
#hub .fd-dl::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 11px;
  border-left: 1.5px solid rgb(var(--accent-rgb) / 0.5);
  border-bottom: 1.5px solid rgb(var(--accent-rgb) / 0.5);
}
#hub .fd-dl > i { font-style: normal; font-size: max(var(--type-floor), 10px); letter-spacing: 0.14em; color: var(--text); }
#hub .fd-dl > small { font-size: max(var(--type-floor), 9.5px); letter-spacing: 0.1em; color: var(--dim); }
#hub .fd-dx { display: flex; flex-direction: column; gap: 1px; padding-top: 6px; border-top: 1px solid var(--edge); }
#hub .fd-dx > b { font-weight: 500; color: var(--hero-ink); letter-spacing: 0.04em; overflow-wrap: anywhere; }
#hub .fd-dx > i { font-style: normal; font-size: max(var(--type-floor), 9.5px); letter-spacing: 0.1em; color: var(--dim); }
#hub .fd-dd { margin: 2px 0 0; font-size: max(var(--type-floor), 11px); line-height: 1.5; color: var(--dim); }
#hub .wd-plate { display: block; text-align: center; font-weight: 500; color: var(--hero-ink); letter-spacing: 0.08em; }
#hub .wd-pl { display: block; text-align: center; color: var(--dim); font-size: max(var(--type-floor), 10px); letter-spacing: 0.12em; }

/* --- RANKS: the SEASONS column of a wide HONOUR board (R1907) ----------
   Card 02 of mockup round 092: a wide pane states the seasons of each
   account after the name, then the Honour rank title (R3234), then the
   Honour, under one head row. The TITLE cell holds the longest title, so
   SEASONS stands left of it on all three ladders. */
#hub .row.rk-sn .sn {
  flex: none; width: 8ch; text-align: center;
  color: var(--dim); white-space: nowrap; font-variant-numeric: tabular-nums;
}
#hub .row.rk-sn .tt {
  flex: none; width: 22ch; overflow: hidden; text-overflow: ellipsis;
  color: var(--text); white-space: nowrap; letter-spacing: 0.04em;
}
#hub .row.rk-sn .v { min-width: 10ch; text-align: right; }
#hub .row.rk-hd { padding-top: 0; }
#hub .row.rk-hd :is(.k, .sn, .tt, .v) {
  font-size: max(var(--type-floor), 9px); letter-spacing: 0.14em; color: var(--dim);
}
/* --- end of RANKS: the SEASONS column ---------------------------------- */

/* --- MODDING: the options as pictures (R1909, §16.3) ---------------------
   Card 01 of mockup round 092. THEMES opens on THE WHOLE LOOK, then one
   block per category: chips with swatches for UI, a scale of pictures for
   WORLD, MARKS and CHART, a list with ▶ for MUSIC and SFX. EFFECTS shows
   the slot map and WHAT FITS the picked slot; a narrow pane opens WHAT
   FITS as a sub-page in a card of its own (R1919). A picture is a frame of the theme
   preview (`mods-pictures.ts`) in a box of the 16 / 10 frame; the box is
   empty, on the deep ground, until the frame is baked. Every option is a
   button inside a card, so these rules take back the look of a card's own
   button with a class of their own. */
#hub .mthemes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 6px 20px;
}
#hub .mblk { min-width: 0; padding-bottom: 10px; }
#hub .mwhole { padding-bottom: 14px; margin-bottom: 4px; border-bottom: 1px solid rgb(var(--accent-rgb) / 0.12); }
/* The lede under MIX BY CATEGORY invites a mix, open or closed (R2154). */
#hub .mmix-lede { margin: 8px 0 10px; }
#hub .mhead { border-bottom: 0; }
#hub .mhead .val {
  flex: none;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 0.14em;
  color: var(--accent);
}
#hub .mhead .val.locked { color: var(--dim); }
#hub .mhead .val.suspended { color: var(--dim); text-decoration: line-through; }
/* On a telephone a head is the grid of a MODDING row (R818). There the
   cap of 50 % read the auto track of the value itself, so the value cut
   to one letter. The track of the value takes at most half the head, and
   the value wraps at a space and never cuts a word (R2208). */
@media (max-width: 900px) {
  #hub .mhead { grid-template-columns: minmax(0, 1fr) fit-content(50%); }
  #hub .mhead .val { max-width: none; overflow: visible; white-space: normal; text-align: end; }
}
/* The look shared by every option press. */
#hub .card :is(.mlrow, .mslot, .mfxr) {
  min-height: 0;
  padding: 6px;
  font: inherit;
  font-size: max(var(--type-floor), 10px);
  letter-spacing: 0.06em;
  text-align: left;
  color: var(--text);
  background: rgb(var(--deep-rgb) / 0.5);
  border: 1px solid var(--edge);
  border-radius: 6px;
  cursor: pointer;
}
#hub .card :is(.mlrow, .mslot, .mfxr).on {
  border-color: var(--accent);
  background: rgb(var(--accent-rgb) / 0.12);
}
#hub .card :is(.mlrow, .mfxr).lk { color: var(--dim); }
#hub :is(.mlrow, .mfxr) b { font-weight: 500; color: inherit; }
#hub :is(.mlrow, .mfxr).on b { color: var(--hero-ink); }
#hub .mlrow em { font-style: normal; color: var(--accent); }
#hub .mtier { font-size: max(var(--type-floor), 9px); letter-spacing: 0.1em; color: var(--dim); }
/* The picture box. A stale picture is the newest picture of the option
   while the picture of the look in force is baked. */
#hub .mpic {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 4px;
  background: rgb(var(--deep-rgb));
}
#hub .mpic img { display: block; width: 100%; height: 100%; object-fit: cover; }
#hub .mpic.stale img { opacity: 0.45; }
#hub .mpic.g { display: grid; place-items: center; color: var(--dim); font-size: 16px; }
/* The scale: one preview tile of the choice kit per vendored option, from
   quieter to louder (R1909, R2011). The picture fills the top of the tile. */
#hub .mblk .ch-tiles { margin-top: 4px; }
#hub button.ch-tile > .mpic { border: 0; border-radius: 0; }
#hub .maxis {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: max(var(--type-floor), 9px);
  letter-spacing: 0.1em;
  color: var(--dim);
}
/* The UI options are preview tiles with the swatches of their tokens, and
   CUSTOM a tile with its glyph. */
#hub .msw { display: inline-flex; flex: none; gap: 2px; }
#hub button.ch-tile > .msw { justify-content: center; padding: 10px 0; }
#hub .msw i { display: block; width: 10px; height: 18px; border-radius: 2px; border: 1px solid var(--edge); }
#hub .msw.g { width: 18px; justify-content: center; color: var(--dim); }
#hub .mcn { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
#hub .mcn i { font-style: normal; color: var(--dim); font-size: 12px; letter-spacing: 0; line-height: 1.35; }
/* The list of MUSIC and SFX. */
#hub .mlist { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
#hub .mlrow { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 40px; }
#hub .mplay { flex: none; color: var(--accent); }
/* EFFECTS: the slot map beside WHAT FITS, one over the other where the
   pane holds no two columns. */
#hub .mfx {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 16px 22px;
  align-items: start;
  margin-top: 8px;
}
#hub .mslots, #hub .mfits { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
#hub .mlegend { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: max(var(--type-floor), 9px); letter-spacing: 0.08em; color: var(--dim); }
#hub .mgrp { display: flex; flex-direction: column; gap: 5px; }
#hub .mgh { font-size: max(var(--type-floor), 9px); letter-spacing: 0.14em; color: var(--dim); margin-top: 4px; }
#hub .mgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 5px; }
#hub .mslot { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
#hub .mslot.wide { flex-direction: row; justify-content: space-between; align-items: baseline; gap: 10px; }
#hub .mslot b { font-weight: 500; color: var(--text); }
#hub .mslot i { font-style: normal; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#hub :is(.mslot, .mlegend) .own { color: var(--accent); }
#hub :is(.mslot, .mlegend) .fol { color: var(--text); }
#hub :is(.mslot, .mlegend) .non { color: var(--dim); }
#hub .mfh { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 4px 10px; }
#hub .mfh b { font-weight: 500; letter-spacing: 0.12em; color: var(--text); }
#hub .mfh i { font-style: normal; font-size: max(var(--type-floor), 10px); letter-spacing: 0.1em; color: var(--dim); }
#hub .mfits .mdetail { margin: 0; }
#hub .mfxr {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
}
#hub .mfxr em { font-style: normal; color: var(--accent); white-space: nowrap; }
@media (pointer: coarse) {
  #hub .card :is(.mlrow, .mslot, .mfxr) { min-height: 44px; }
}
/* --- end of MODDING: the options as pictures ------------------------------ */

/* --- MODDING: the close-up under the preview (R1914) ---------------------
   The column holds a second preview under the full one: the map alone,
   nearer. Only the column draws it; the band and a telephone hold the
   full preview alone. */
#hub .mpreview .mclose { margin-top: 12px; }
#hub .mpreview .mclose .mcap { margin-top: 6px; }
/* --- end of MODDING: the close-up ---------------------------------------- */
